// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  brokerageApiV2,
  type BrokerageTagTypes,
} from '@shared/data-access/api-base/citec-brokerage-api';
import type { FetchQueryFn } from '@shared/data-access/queries';
import type * as BrokerageTypes from '@shared/data-access/types/brokerage-types';
import type { SharedTagTypes } from '@shared/data-access/utils';

type EndpointBuilders = EndpointBuilder<
  FetchQueryFn,
  BrokerageTagTypes | SharedTagTypes,
  'brokerageApiV2'
>;

/**
 * Raw Brokerage API - Base Endpoints
 *
 * This API layer provides direct access to the **Citec Brokerage REST API**
 * endpoints without any data transformations, business logic, or multiple request
 * orchestration. These raw endpoints serve as the foundation for processed
 * endpoints in other API files (e.g., `aggregator-api.ts`, `advisory-api.ts`,
 * `proposals-api.ts`).
 *
 * @remarks
 * **Key Characteristics:**
 *
 * - All endpoints use simple `query` functions (no `queryFn` or complex logic)
 * - Direct API calls with no data transformations
 * - No multiple request orchestration
 * - Direct cache of API responses
 * - Endpoints are grouped by **region** in helpers `getXxxEndpoints(build)` and
 *   composed in `endpoints: build => ({ ...getAccountEndpoints(build), ... })`.
 *   Regions: ACCOUNT, ADVISORY, AGGREGATOR, AGGREGATOR-Institutions, ASSETS,
 *   CURRENCY, EQUITY, NOTIFICATION, PORTFOLIO, SETTINGS, WEBHOOKS.
 *
 * **Usage Pattern:** Processed API files should use these raw endpoints internally
 * and add:
 *
 * - Data transformations via `transformResponse`
 * - Multiple request orchestration via `queryFn`
 * - Business logic and validation
 * - Cache tag management
 *
 * **Example:**
 *
 * ```tsx
 * // In a processed API file
 * const processedEndpoint = build.query({
 *   queryFn: async (params, queryApi, _extraOptions, fetchWithBQ) => {
 *     // Use rawApi internally
 *     const result = await fetchWithBQ({
 *       url: '/v2/account/',
 *       method: 'POST',
 *       body: params,
 *     });
 *     // Add transformations, business logic, etc.
 *     return { data: transform(result.data) };
 *   },
 * });
 * ```
 * @see {@link https://citecsolutions.com/apis} for full API reference.
 */
const getAccountEndpoints = (build: EndpointBuilders) => ({
  /**
   * Get account information from the broker user account.
   *
   * Retrieves detailed account information including buying power, cash, equity,
   * portfolio value, and currency for a specific broker account.
   *
   * @remarks
   * This endpoint requires broker configuration (broker_key and type_account) and
   * optionally accepts an account_id to filter results. The account_id is passed in
   * the request body.
   *
   * **Raw endpoint:** `POST /v2/account/`
   * @example
   *
   * ```tsx
   * const { data: account, isLoading } = rawApi.useGetAccountRawQuery({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   account_id: 'DU1234567',
   * });
   *
   * // Access account data
   * console.log(account?.buying_power); // 50000
   * console.log(account?.cash); // 10000
   * console.log(account?.equity); // 60000
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `account_id`: (Optional) Specific account ID to retrieve
   *
   * @returns A {@link BrokerageTypes.GetAccountResponseBody} containing account
   *   information including buying power, cash, equity, portfolio value, and
   *   currency.
   */
  getAccountRaw: build.query<
    BrokerageTypes.GetAccountResponseBody,
    BrokerageTypes.GetAccountParams & BrokerageTypes.GetAccountRequestBody
  >({
    query: ({ broker_key, type_account, ...body }) => ({
      url: `/v2/account/`,
      method: 'POST',
      body,
      params: {
        broker_key,
        type_account,
      },
    }),
  }),

  /**
   * Get all accounts available for a user in a Broker API.
   *
   * Retrieves a list of all account information objects for the authenticated user
   * across all accounts associated with the specified broker configuration.
   *
   * @remarks
   * This endpoint returns comprehensive account information for all accounts,
   * including investment amounts, unrealized capital gains, buying power, and
   * currency-specific values. The currency parameter allows you to specify the
   * currency for account value calculations.
   *
   * **Raw endpoint:** `POST /v2/account/all/`
   * @example
   *
   * ```tsx
   * const { data: accounts, isLoading } = rawApi.useGetAccountAllRawQuery({
   *   broker_key: 'interactive_brokers',
   *   type_account: 'LIVE',
   *   currency: 'USD',
   * });
   *
   * // Iterate over all accounts
   * accounts?.forEach(account => {
   *   console.log(`Account: ${account.name}`);
   *   console.log(`Investment: ${account.investment}`);
   *   console.log(`Buying Power: ${account.buying_power}`);
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `currency`: Currency for account values ('USD' or 'EUR')
   *
   * @returns An array of {@link BrokerageTypes.GetAccountAllResponseBody} containing
   *   detailed account information for all user accounts.
   */
  getAccountAllRaw: build.query<
    BrokerageTypes.GetAccountAllResponseBody,
    BrokerageTypes.GetAccountAllParams
  >({
    query: ({ broker_key, type_account, currency }) => ({
      url: `/v2/account/all/`,
      method: 'POST',
      params: {
        broker_key,
        type_account,
        currency,
      },
    }),
  }),

  /**
   * Get credentials for a specific broker.
   *
   * Retrieves the stored authentication credentials (tokens, secrets, etc.) for a
   * specific broker configuration. These credentials are used to authenticate API
   * requests to the broker's platform.
   *
   * @remarks
   * This endpoint returns sensitive authentication information including tokens,
   * token secrets, OAuth verifiers, and expiration dates. Handle this data with
   * appropriate security measures.
   *
   * **Raw endpoint:** `GET /v2/account/brokers/credentials/`
   * @example
   *
   * ```tsx
   * const { data: credentials, isLoading } =
   *   rawApi.useGetAccountBrokersCredentialsRawQuery({
   *     broker_key: 'alpaca',
   *   });
   *
   * // Access credential data
   * if (credentials) {
   *   console.log(`Token: ${credentials.token}`);
   *   console.log(`Expires: ${credentials.expiration_date}`);
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *
   * @returns A {@link BrokerageTypes.GetAccountBrokersCredentialsResponseBody}
   *   containing authentication credentials including tokens, secrets, and
   *   expiration information.
   */
  getAccountBrokersCredentialsRaw: build.query<
    BrokerageTypes.GetAccountBrokersCredentialsResponseBody,
    BrokerageTypes.GetAccountBrokersCredentialsParams
  >({
    query: ({ broker_key }) => ({
      url: `/v2/account/brokers/credentials/`,
      method: 'GET',
      params: {
        broker_key,
      },
    }),
  }),

  /**
   * Save credentials for a specific broker.
   *
   * Stores authentication credentials (tokens, secrets, OAuth verifiers, etc.) for
   * a specific broker configuration. This is typically used during the broker
   * connection setup process.
   *
   * @remarks
   * This endpoint accepts sensitive authentication information and stores it
   * securely. After successful storage, it returns the connection status indicating
   * whether the broker connection is active.
   *
   * **Raw endpoint:** `POST /v2/account/brokers/credentials/`
   * @example
   *
   * ```tsx
   * const [saveCredentials, { isLoading, isSuccess }] =
   *   rawApi.useCreateAccountBrokersCredentialsRawMutation();
   *
   * await saveCredentials({
   *   broker_key: 'alpaca',
   *   token: 'your-api-key',
   *   token_secret: 'your-api-secret',
   *   expiration_date: 1698185181516,
   * });
   *
   * if (isSuccess) {
   *   console.log('Credentials saved successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `token`: API token or key
   *   - `token_secret`: API secret (if applicable)
   *   - `oauth_verifier`: OAuth verifier (if applicable)
   *   - `expiration_date`: Token expiration timestamp
   *
   * @returns A {@link BrokerageTypes.CreateAccountBrokersCredentialsResponseBody}
   *   containing the broker connection status (connected, type_account, token).
   */
  createAccountBrokersCredentialsRaw: build.mutation<
    BrokerageTypes.CreateAccountBrokersCredentialsResponseBody,
    BrokerageTypes.CreateAccountBrokersCredentialsParams &
      BrokerageTypes.CreateAccountBrokersCredentialsRequestBody
  >({
    query: ({ broker_key, ...body }) => ({
      url: `/v2/account/brokers/credentials/`,
      method: 'POST',
      body,
      params: {
        broker_key,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Get all broker keys saved in the user account.
   *
   * Retrieves a list of all broker keys that have been configured and saved for the
   * authenticated user. This is useful for determining which brokers are available
   * for the current user.
   *
   * @remarks
   * This endpoint returns a simple array of broker key strings. Use this endpoint
   * to populate broker selection dropdowns or to check which brokers are configured
   * before making other API calls.
   *
   * **Raw endpoint:** `GET /v2/account/brokers/keys/`
   * @example
   *
   * ```tsx
   * const { data: brokerKeys, isLoading } =
   *   rawApi.useGetAccountBrokersKeysRawQuery();
   *
   * // Check available brokers
   * if (brokerKeys) {
   *   console.log('Available brokers:', brokerKeys);
   *   // ['alpaca', 'interactive_brokers']
   * }
   * ```
   *
   * @returns An array of broker key strings (e.g., ['alpaca',
   *   'interactive_brokers', 'tradier']).
   */
  getAccountBrokersKeysRaw: build.query<string[], void>({
    query: () => ({
      url: `/v2/account/brokers/keys/`,
      method: 'GET',
    }),
  }),

  /**
   * Get detailed account information by account ID.
   *
   * Retrieves comprehensive account information for a specific account, including
   * investment amounts, unrealized capital gains, buying power, cash, and
   * currency-specific values. This endpoint provides more detailed information than
   * the basic account endpoint.
   *
   * @remarks
   * This endpoint requires an account_id in the request body to identify the
   * specific account. The currency parameter allows you to specify the currency for
   * account value calculations and local currency conversions.
   *
   * **Raw endpoint:** `POST /v2/account/info/`
   * @example
   *
   * ```tsx
   * const { data: accountInfo, isLoading } = rawApi.useGetAccountInfoRawQuery({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   currency: 'USD',
   *   account_id: 'DU1234567',
   * });
   *
   * // Access detailed account information
   * if (accountInfo) {
   *   console.log(`Account: ${accountInfo.name}`);
   *   console.log(`AUM: ${accountInfo.aum}`);
   *   console.log(`Investment: ${accountInfo.investment}`);
   *   console.log(`Unrealized Gains: ${accountInfo.unrealized_capital_gains}`);
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `currency`: Currency for account values ('USD' or 'EUR')
   *   - `account_id`: Specific account ID to retrieve information for
   *
   * @returns A {@link BrokerageTypes.GetAccountInfoResponseBody} containing detailed
   *   account information including AUM, investment, buying power, cash, unrealized
   *   gains, and local currency values.
   */
  getAccountInfoRaw: build.query<
    BrokerageTypes.GetAccountInfoResponseBody,
    BrokerageTypes.BrokerConfigWithCurrencyParam &
      BrokerageTypes.GetAccountInfoRequestBody
  >({
    query: ({ broker_key, type_account, currency, ...body }) => ({
      url: `/v2/account/info/`,
      method: 'POST',
      body,
      params: {
        broker_key,
        type_account,
        currency,
      },
    }),
  }),

  /**
   * Get transactions from the broker user account.
   *
   * Retrieves a list of all transactions (trades, deposits, withdrawals, dividends,
   * etc.) for a specific broker account. The account_id is optional; if not
   * provided, transactions for all accounts may be returned.
   *
   * @remarks
   * This endpoint uses POST method to allow filtering by account_id in the request
   * body. Transactions include details such as date, description, symbol, amount,
   * quantity, price, type, and currency.
   *
   * **Raw endpoint:** `POST /v2/account/transaction/`
   * @example
   *
   * ```tsx
   * const { data: transactions, isLoading } =
   *   rawApi.useGetAccountTransactionRawQuery({
   *     broker_key: 'alpaca',
   *     type_account: 'PAPER',
   *     account_id: 'DU1234567',
   *   });
   *
   * // Process transactions
   * transactions?.forEach(transaction => {
   *   console.log(`${transaction.date}: ${transaction.description}`);
   *   console.log(`Amount: ${transaction.amount} ${transaction.currency}`);
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `account_id`: (Optional) Specific account ID to filter transactions
   *
   * @returns An array of {@link BrokerageTypes.GetAccountTransactionResponseBody}
   *   containing transaction details including date, description, symbol, amount,
   *   quantity, price, type, and currency.
   */
  getAccountTransactionRaw: build.query<
    BrokerageTypes.GetAccountTransactionResponseBody,
    BrokerageTypes.GetAccountTransactionParams &
      BrokerageTypes.GetAccountTransactionRequestBody
  >({
    query: ({ broker_key, type_account, ...body }) => ({
      url: `/v2/account/transaction/`,
      method: 'POST',
      body,
      params: {
        broker_key,
        type_account,
      },
    }),
  }),
});

const getAdvisoryEndpoints = (build: EndpointBuilders) => ({
  /**
   * Get investment positions for advisory accounts (raw).
   *
   * Returns positions (holdings) for the given accounts. Each item includes
   * institution_id, iban, last_sync, and an array of holdings (asset details,
   * quantity, value).
   *
   * @remarks
   * **Payload:** Request body includes `ibans` and optional `client_id`.
   *
   * - If `ibans` is omitted or undefined, the backend returns all positions for the
   *   request context (e.g. all accounts the user can access).
   * - If `ibans` is an array, only positions for those IBANs are returned. The
   *   `add_positions` query parameter controls whether extra position data is
   *   included.
   *
   * **Raw endpoint:** `POST /v2/advisory/accounts/positions/`
   * @example
   *
   * ```tsx
   * // All positions (ibans undefined)
   * const { data } = rawApi.useGetAdvisoryAccountsPositionsRawQuery({
   *   client_id: 123,
   *   add_positions: false,
   * });
   *
   * // Only specific IBANs
   * const { data } = rawApi.useGetAdvisoryAccountsPositionsRawQuery({
   *   ibans: ['ES1234567890123456789012'],
   *   client_id: 123,
   *   add_positions: false,
   * });
   * ```
   *
   * @param params.ibans - Optional. IBANs to fetch; if undefined, all data for the
   *   context
   * @param params.client_id - Optional. Client ID to filter by
   * @param params.add_positions - Optional. Include extra position data
   * @returns An array of
   *   {@link BrokerageTypes.GetAdvisoryAccountsPositionsResponseBody}
   */
  getAdvisoryAccountsPositionsRaw: build.query<
    BrokerageTypes.GetAdvisoryAccountsPositionsResponseBody,
    BrokerageTypes.GetAdvisoryAccountsPositionsParams &
      BrokerageTypes.GetAdvisoryAccountsPositionsRequestBody
  >({
    query: ({ add_positions, ...body }) => ({
      url: `/v2/advisory/accounts/positions/`,
      method: 'POST',
      body,
      params: {
        add_positions,
      },
    }),
  }),

  /**
   * Get account transactions for advisory accounts (raw).
   *
   * Retrieves transaction history for accounts, starting from a given date.
   * Response is grouped by institution and IBAN (date, description, amount,
   * quantity, price, type, asset type, currency).
   *
   * @remarks
   * **Payload:** Request body includes `start_date`, optional `ibans` and
   * `client_id`.
   *
   * - If `ibans` is omitted or undefined, the backend returns all transactions for
   *   the request context from `start_date`.
   * - If `ibans` is an array, only transactions for those IBANs are returned. The
   *   `add_positions` query parameter can include position information.
   *
   * **Raw endpoint:** `POST /v2/advisory/accounts/transactions/`
   * @example
   *
   * ```tsx
   * const { data } = rawApi.useGetAdvisoryAccountsTransactionsRawQuery({
   *   start_date: '2024-01-01',
   *   client_id: 123,
   *   add_positions: false,
   * });
   * ```
   *
   * @param params.start_date - Start date for transactions (ISO 8601)
   * @param params.ibans - Optional. IBANs to fetch; if undefined, all for context
   * @param params.client_id - Optional. Client ID to filter by
   * @param params.add_positions - Optional. Include position information
   * @returns An array of
   *   {@link BrokerageTypes.GetAdvisoryAccountsTransactionsResponseBody}
   */
  getAdvisoryAccountsTransactionsRaw: build.query<
    BrokerageTypes.GetAdvisoryAccountsTransactionsResponseBody,
    BrokerageTypes.GetAdvisoryAccountsTransactionsParams &
      BrokerageTypes.GetAdvisoryAccountsTransactionsRequestBody
  >({
    query: ({ add_positions, ...body }) => ({
      url: `/v2/advisory/accounts/transactions/`,
      method: 'POST',
      body,
      params: {
        add_positions,
      },
    }),
  }),

  /**
   * Get historical balances for advisory accounts (raw).
   *
   * Retrieves historical balance snapshots (cash, debt, investment, net value) for
   * accounts over a date range. Each item includes institution_id, iban, last_sync
   * and historical_balances (dates, cash, debt, investment, net_value arrays).
   *
   * @remarks
   * **Payload:** Request body includes `currency`, `start_date`, `end_date`, and
   * optional `ibans` and `client_id`.
   *
   * - If `ibans` is omitted or undefined, the backend returns all historical balances
   *   for the request context in the date range.
   * - If `ibans` is an array, only data for those IBANs is returned.
   *
   * **Raw endpoint:** `POST /v2/advisory/accounts/balances/historical/`
   * @example
   *
   * ```tsx
   * const { data } = rawApi.useGetAdvisoryAccountsBalancesHistoricalRawQuery({
   *   currency: 'EUR',
   *   start_date: '2024-01-01',
   *   end_date: '2024-12-31',
   *   client_id: 123,
   * });
   * ```
   *
   * @param body.currency - Currency for balance values
   * @param body.start_date - Start date (ISO 8601: YYYY-MM-DD)
   * @param body.end_date - End date (ISO 8601: YYYY-MM-DD)
   * @param body.ibans - Optional. IBANs to fetch; if undefined, all for context
   * @param body.client_id - Optional. Client ID to filter by
   * @returns An array of
   *   {@link BrokerageTypes.GetAdvisoryAccountsBalancesHistoricalResponseBody}
   */
  getAdvisoryAccountsBalancesHistoricalRaw: build.query<
    BrokerageTypes.GetAdvisoryAccountsBalancesHistoricalResponseBody,
    BrokerageTypes.GetAdvisoryAccountsBalancesHistoricalRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/accounts/balances/historical/`,
      method: 'POST',
      body,
    }),
  }),

  /**
   * Get debt data for advisory accounts (raw).
   *
   * Returns credit lines and loans for all accessible accounts together with
   * historical debt series classified by maturity.
   *
   * @remarks
   * **Raw endpoint:** `POST /v2/advisory/accounts/debts/`
   * @example
   *
   * ```tsx
   * const { data } = rawApi.useGetAdvisoryAccountsDebtRawQuery({
   *   start_date: '2026-02-19',
   *   end_date: '2026-03-19',
   *   currency: 'EUR',
   *   client_id: 123,
   * });
   * ```
   *
   * @param body.start_date - Start of the time window (ISO 8601)
   * @param body.end_date - End of the time window (ISO 8601)
   * @param body.currency - Target currency for monetary values
   * @param body.ibans - Optional. IBANs to filter; if omitted, all accessible
   * @param body.client_id - Optional. Client ID (advisor context)
   * @returns {@link BrokerageTypes.GetAdvisoryAccountsDebtResponseBody}
   */
  getAdvisoryAccountsDebtRaw: build.query<
    BrokerageTypes.GetAdvisoryAccountsDebtResponseBody,
    BrokerageTypes.GetAdvisoryAccountsDebtRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/accounts/debts/`,
      method: 'POST',
      body,
    }),
  }),

  /**
   * Bulk upload manual institution account data (raw).
   *
   * Creates or updates a manual Citec institution with accounts, positions,
   * transactions, and debts in a single request.
   *
   * @remarks
   * **Raw endpoint:** `POST /v2/advisory/accounts/citec/`
   * @example
   *
   * ```tsx
   * const [createAccountsCitec, { isLoading }] =
   *   rawApi.useCreateAdvisoryAccountsCitecRawMutation();
   *
   * await createAccountsCitec({
   *   institution: { name: 'My Bank', logo_url: 'https://example.com/logo.png' },
   *   accounts: [{ iban: 'ES123', name: 'Checking', currency: 'EUR', ... }],
   * });
   * ```
   *
   * @param body - See {@link BrokerageTypes.CreateAdvisoryAccountsCitecRequestBody}
   * @returns {@link BrokerageTypes.CreateAdvisoryAccountsCitecResponseBody}
   */
  createAdvisoryAccountsCitecRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryAccountsCitecResponseBody,
    BrokerageTypes.CreateAdvisoryAccountsCitecRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/accounts/citec/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Partially update manual institution account data.
   *
   * Upserts institution metadata and optional account, position, transaction, and
   * debt rows. The institution `id` in the body identifies the target manual bank.
   *
   * @remarks
   * **Raw endpoint:** `POST /v2/advisory/accounts/citec/`
   * @example
   *
   * ```tsx
   * const [updateAccountsCitec, { isLoading }] =
   *   rawApi.useUpdateAdvisoryAccountsCitecRawMutation();
   *
   * await updateAccountsCitec({
   *   institution: {
   *     id: 'inst_manual_1',
   *     name: 'Updated Bank',
   *     logo_url: 'https://example.com/logo.png',
   *   },
   * });
   * ```
   *
   * @param body - See {@link BrokerageTypes.UpdateAdvisoryAccountsCitecRequestBody}
   * @returns {@link BrokerageTypes.UpdateAdvisoryAccountsCitecResponseBody}
   */
  updateAdvisoryAccountsCitecRaw: build.mutation<
    BrokerageTypes.UpdateAdvisoryAccountsCitecResponseBody,
    BrokerageTypes.UpdateAdvisoryAccountsCitecRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/accounts/citec/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * List all clients for the authenticated user.
   *
   * Retrieves a list of all clients associated with the authenticated user,
   * including their profile information, status, and associated institutions.
   *
   * @remarks
   * This endpoint returns comprehensive client information including email, name,
   * status (active, pending, inactive), creation date, and linked financial
   * institutions. Use this endpoint to populate client selection lists or to
   * display client overview information.
   *
   * **Raw endpoint:** `GET /v2/advisory/clients/`
   * @example
   *
   * ```tsx
   * const { data: clients, isLoading } =
   *   rawApi.useGetAdvisoryClientsRawQuery();
   *
   * // Display client list
   * clients?.forEach(client => {
   *   console.log(`Client ID: ${client.id}`);
   *   console.log(`Name: ${client.name || 'N/A'}`);
   *   console.log(`Email: ${client.email}`);
   *   console.log(`Status: ${client.status}`);
   *   console.log(`Institutions: ${client.institutions?.length || 0}`);
   * });
   * ```
   *
   * @returns An array of {@link BrokerageTypes.GetAdvisoryClientsResponseBody}
   *   containing client information including email, name, ID, citec_user_id,
   *   status, created_at, and associated institutions.
   */
  getAdvisoryClientsRaw: build.query<
    BrokerageTypes.GetAdvisoryClientsResponseBody,
    BrokerageTypes.GetAdvisoryClientsParams | void
  >({
    query: params => ({
      url: `/v2/advisory/clients/`,
      method: 'GET',
      params: params ?? {},
    }),
  }),

  /**
   * Update the profile of the current client.
   *
   * Updates profile information for the authenticated user's client profile. Only
   * editable fields can be updated (name fields, birth date, contact information,
   * address, etc.).
   *
   * @remarks
   * This endpoint updates the current authenticated user's client profile. All
   * fields in the request body are optional - only provided fields will be updated.
   * The endpoint returns void on success (204 response).
   *
   * **Raw endpoint:** `PATCH /v2/advisory/clients/`
   * @example
   *
   * ```tsx
   * const [updateProfile, { isLoading, isSuccess }] =
   *   rawApi.useUpdateAdvisoryClientsRawMutation();
   *
   * await updateProfile({
   *   first_name: 'John',
   *   first_surname: 'Doe',
   *   phone_number: '+1234567890',
   *   address: '123 Main St',
   *   city: 'New York',
   *   country: 'USA',
   *   postal_code: '10001',
   * });
   *
   * if (isSuccess) {
   *   console.log('Profile updated successfully');
   * }
   * ```
   *
   * @param body - An object containing the fields to update:
   *
   *   - `first_name`: (Optional) Client's first or given name
   *   - `second_name`: (Optional) Client's second or middle name
   *   - `first_surname`: (Optional) Client's first (paternal) surname
   *   - `second_surname`: (Optional) Client's second (maternal) surname
   *   - `birth_date`: (Optional) Date of birth (ISO 8601 format)
   *   - `phone_number`: (Optional) Contact phone number
   *   - `address`: (Optional) Street address
   *   - `city`: (Optional) City
   *   - `nif_nie`: (Optional) Tax identification number
   *   - `country`: (Optional) Country code
   *   - `postal_code`: (Optional) Postal/ZIP code
   *
   * @returns Void response (204) upon successful update.
   */
  updateAdvisoryClientsRaw: build.mutation<
    BrokerageTypes.UpdateAdvisoryClientsResponseBody,
    BrokerageTypes.UpdateAdvisoryClientsRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/clients/`,
      method: 'PATCH',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Create a new client account and enable financial institution linking.
   *
   * Creates a new client account associated with the authenticated user and enables
   * the ability to link financial institutions to this client.
   *
   * @remarks
   * This endpoint creates a new client record with the provided email and optional
   * name fields. After creation, the client can link financial institutions (banks,
   * brokerages, etc.) to their account. The client will be created with a 'pending'
   * status initially.
   *
   * **Raw endpoint:** `POST /v2/advisory/clients/`
   * @example
   *
   * ```tsx
   * const [createClient, { isLoading, data: newClient }] =
   *   rawApi.useCreateAdvisoryClientsRawMutation();
   *
   * await createClient({
   *   email: 'john.doe@example.com',
   *   first_name: 'John',
   *   first_surname: 'Doe',
   * });
   *
   * if (newClient) {
   *   console.log(`Client created with ID: ${newClient.id}`);
   *   console.log(`Status: ${newClient.status}`);
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `email`: Client's email address (required)
   *   - `first_name`: (Optional) Client's first or given name
   *   - `second_name`: (Optional) Client's second or middle name
   *   - `first_surname`: (Optional) Client's first (paternal) surname
   *   - `second_surname`: (Optional) Client's second (maternal) surname
   *
   * @returns A {@link BrokerageTypes.CreateAdvisoryClientsResponseBody} containing
   *   the newly created client information including ID, email, name,
   *   citec_user_id, status, created_at, and institutions array.
   */
  createAdvisoryClientsRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryClientsResponseBody,
    BrokerageTypes.CreateAdvisoryClientsRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/clients/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Verify if a client was invited by a Citec user (Retail Client Access).
   *
   * Checks whether a client with the specified email address has been invited by a
   * Citec user and has access to retail features. This is used for access control
   * and feature gating.
   *
   * @remarks
   * This endpoint is used to verify retail access eligibility for a client email.
   * It's primarily used for access control and determining which features a client
   * can access.
   *
   * **Raw endpoint:** `GET /v2/advisory/clients/access/`
   * @example
   *
   * ```tsx
   * const { data: accessInfo, isLoading } =
   *   rawApi.useGetAdvisoryClientsAccessRawQuery({
   *     client_email: 'john.doe@example.com',
   *   });
   *
   * // Check access status
   * if (accessInfo) {
   *   console.log('Client has retail access');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `client_email`: Email address of the client to verify access for
   *
   * @returns Access verification information (response structure may vary).
   */
  getAdvisoryClientsAccessRaw: build.query<
    BrokerageTypes.GetAdvisoryClientsAccessResponseBody,
    BrokerageTypes.GetAdvisoryClientsAccessParams
  >({
    query: ({ client_email }) => ({
      url: `/v2/advisory/clients/access/`,
      method: 'GET',
      params: {
        client_email,
      },
    }),
  }),

  /**
   * Get the current Citec user's client profile.
   *
   * Retrieves the complete client profile for the authenticated Citec user,
   * including personal information, contact details, and associated advisory
   * proposal if available.
   *
   * @remarks
   * This endpoint returns the client profile for the currently authenticated user
   * without requiring a specific client_id. It includes comprehensive profile
   * information including the name fields, birth date, contact information,
   * address, and any associated advisory proposal.
   *
   * **Raw endpoint:** `GET /v2/advisory/clients/profile/`
   * @example
   *
   * ```tsx
   * const { data: profile, isLoading } =
   *   rawApi.useGetAdvisoryClientsProfileRawQuery();
   *
   * // Access profile information
   * if (profile) {
   *   console.log(`Name: ${profile.name}`);
   *   console.log(`Email: ${profile.email}`);
   *   console.log(`Phone: ${profile.phone_number || 'N/A'}`);
   *   console.log(`Address: ${profile.address || 'N/A'}`);
   *   if (profile.advisory_proposal) {
   *     console.log(`Has active proposal: ${profile.advisory_proposal.id}`);
   *   }
   * }
   * ```
   *
   * @returns A {@link BrokerageTypes.GetAdvisoryClientsProfileResponseBody}
   *   containing the client profile including the name fields, email, contact
   *   information, address, and associated advisory proposal.
   */
  getAdvisoryClientsProfileRaw: build.query<
    BrokerageTypes.GetAdvisoryClientsProfileResponseBody,
    void
  >({
    query: () => ({
      url: `/v2/advisory/clients/profile/`,
      method: 'GET',
    }),
  }),

  /**
   * Delete a Citec user client association.
   *
   * Removes the association between a specific client and the authenticated Citec
   * user. This disassociates the client from the user's account but does not delete
   * the client record itself.
   *
   * @remarks
   * This endpoint removes the link between a client and the authenticated user. The
   * client record may still exist in the system, but it will no longer be
   * associated with this user. Use with caution as this action may affect access to
   * client data and associated institutions.
   *
   * **Raw endpoint:** `DELETE /v2/advisory/clients/{client_id}/`
   * @example
   *
   * ```tsx
   * const [deleteClient, { isLoading, isSuccess }] =
   *   rawApi.useDeleteAdvisoryClientsClientIdRawMutation();
   *
   * await deleteClient({ client_id: 123 });
   *
   * if (isSuccess) {
   *   console.log('Client association removed successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `client_id`: ID of the client to disassociate from the user
   *
   * @returns Void response (204) upon successful deletion.
   */
  deleteAdvisoryClientsClientIdRaw: build.mutation<
    BrokerageTypes.DeleteAdvisoryClientsClientIdResponseBody,
    BrokerageTypes.DeleteAdvisoryClientsClientIdParams
  >({
    query: ({ client_id }) => ({
      url: `/v2/advisory/clients/${client_id}/`,
      method: 'DELETE',
    }),
    invalidatesTags: [],
  }),

  /**
   * Get the profile of a specific client by client ID.
   *
   * Retrieves comprehensive client profile information for a specific client,
   * including personal information, contact details, associated institutions, and
   * risk profile.
   *
   * @remarks
   * This endpoint requires a specific `client_id` to retrieve the profile. It
   * returns more detailed information than the basic client list endpoint,
   * including risk profile and all associated institutions with their account
   * details.
   *
   * **Raw endpoint:** `GET /v2/advisory/clients/{client_id}/profile/`
   * @example
   *
   * ```tsx
   * const { data: clientProfile, isLoading } =
   *   rawApi.useGetAdvisoryClientsClientIdProfileRawQuery({
   *     client_id: 123,
   *   });
   *
   * // Access detailed profile
   * if (clientProfile) {
   *   console.log(`Client: ${clientProfile.name}`);
   *   console.log(`Email: ${clientProfile.email}`);
   *   console.log(`Risk Profile: ${clientProfile.risk_profile || 'N/A'}`);
   *   console.log(`Institutions: ${clientProfile.institutions?.length || 0}`);
   *   clientProfile.institutions?.forEach(inst => {
   *     console.log(`  - ${inst.name} (${inst.provider})`);
   *   });
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `client_id`: ID of the client to retrieve profile for
   *
   * @returns A {@link BrokerageTypes.GetAdvisoryClientsClientIdProfileResponseBody}
   *   containing detailed client profile including personal information, contact
   *   details, institutions, and risk profile.
   */
  getAdvisoryClientsClientIdProfileRaw: build.query<
    BrokerageTypes.GetAdvisoryClientsClientIdProfileResponseBody,
    BrokerageTypes.GetAdvisoryClientsClientIdProfileParams
  >({
    query: ({ client_id }) => ({
      url: `/v2/advisory/clients/${client_id}/profile/`,
      method: 'GET',
    }),
  }),

  /**
   * Get all available linked institutions with their accounts.
   *
   * Retrieves a list of all financial institutions (banks, brokerages, etc.) that
   * are linked to the authenticated user's account, including their account
   * details, sync status, and available products.
   *
   * @remarks
   * This endpoint returns comprehensive institution information including provider
   * type (Plaid, Tink, Fintech), last sync timestamp, available products (accounts,
   * cards, loans, portfolios), and any error information. Use this to display the
   * user's connected financial institutions.
   *
   * **Raw endpoint:** `GET /v2/advisory/institutions/`
   * @example
   *
   * ```tsx
   * const { data: institutions, isLoading } =
   *   rawApi.useGetAdvisoryInstitutionsRawQuery();
   *
   * // Display institutions
   * institutions?.forEach(institution => {
   *   console.log(`Institution: ${institution.name}`);
   *   console.log(`Provider: ${institution.provider}`);
   *   console.log(`Last sync: ${institution.last_sync || 'Never'}`);
   *   if (institution.error) {
   *     console.log(`Error: ${institution.error}`);
   *   }
   * });
   * ```
   *
   * @returns An array of {@link BrokerageTypes.GetAdvisoryInstitutionsResponseBody}
   *   containing institution information including ID, name, provider, logo URL,
   *   last sync timestamp, available products, and error information.
   */
  getAdvisoryInstitutionsRaw: build.query<
    BrokerageTypes.GetAdvisoryInstitutionsResponseBody,
    BrokerageTypes.GetAdvisoryInstitutionsParams | void
  >({
    query: (params = {}) => ({
      url: `/v2/advisory/institutions/`,
      method: 'GET',
      params: {
        client_id: params?.client_id,
      },
    }),
  }),

  /**
   * Search advisory institutions by query and country.
   *
   * Lists bank institutions available to connect from the advisory catalog.
   *
   * **Raw endpoint:** `GET /v2/advisory/institutions/search/`
   *
   * @param params.q - Optional search query
   * @param params.country - Optional country code
   * @returns An array of advisory institutions from the provider catalog.
   */
  searchAdvisoryInstitutionsRaw: build.query<
    BrokerageTypes.SearchAdvisoryInstitutionsResponseBody,
    BrokerageTypes.SearchAdvisoryInstitutionsParams
  >({
    query: ({ q, country }) => ({
      url: `/v2/advisory/institutions/search/`,
      method: 'GET',
      params: {
        q,
        country,
      },
    }),
  }),

  /**
   * Create a new institution connection for a specific provider.
   *
   * Establishes a connection to a financial institution by exchanging provider
   * credentials (e.g., Plaid public token) for an access token and creating the
   * institution record.
   *
   * @remarks
   * This endpoint is used during the institution linking flow. It accepts provider-
   * specific credentials (public token for Plaid, authorization code for Tink,
   * etc.) and creates the institution connection. The provider type must match the
   * credentials format.
   *
   * **Raw endpoint:** `POST /v2/advisory/institutions/`
   * @example
   *
   * ```tsx
   * const [createInstitution, { isLoading, data: newInstitution }] =
   *   rawApi.useCreateAdvisoryInstitutionsRawMutation();
   *
   * await createInstitution({
   *   public_token: 'public-sandbox-abc123',
   *   provider: 'plaid',
   * });
   *
   * if (newInstitution) {
   *   console.log(`Institution created: ${newInstitution.name}`);
   *   console.log(`Status: ${newInstitution.status}`);
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `public_token`: Public token from the provider (Plaid, Tink, etc.)
   *   - `code_verifier`: (Optional) OAuth code verifier for PKCE flow
   *   - `provider`: Provider type ('plaid', 'tink', or 'fintech')
   *
   * @returns A {@link BrokerageTypes.CreateAdvisoryInstitutionsResponseBody}
   *   containing the newly created institution information including ID, name,
   *   status, provider, and logo URL.
   */
  createAdvisoryInstitutionsRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryInstitutionsResponseBody,
    BrokerageTypes.CreateAdvisoryInstitutionsRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/institutions/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Create a link token for the Plaid Link flow.
   *
   * Generates a link token that can be used to initialize the Plaid Link component
   * for connecting a financial institution. The link token is required to start the
   * Plaid Link authentication flow.
   *
   * @remarks
   * Exposed as a **mutation** (not a query) because the link token is generated at
   * request time and must not be cached—each call produces a new token. This
   * endpoint generates a temporary link token that is used by the Plaid Link SDK to
   * authenticate and connect a financial institution. The token is typically
   * short-lived and should be used immediately after generation. An optional
   * `institution_id` can be provided to pre-select a specific institution.
   *
   * **Raw endpoint:** `GET /v2/advisory/institutions/link/`
   * @example
   *
   * ```tsx
   * const [getLinkToken, { data: linkTokenData, isLoading }] =
   *   rawApi.useGetAdvisoryInstitutionsLinkRawMutation();
   *
   * // Call when opening Plaid Link (e.g. on button click)
   * const handleLink = async () => {
   *   const result = await getLinkToken({
   *     institution_id: 'ins_1234567890', // Optional
   *   }).unwrap();
   *   const { link_token } = result;
   *   // plaidLink.open({ token: link_token });
   * };
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: (Optional) Pre-select a specific institution ID
   *
   * @returns A {@link BrokerageTypes.GetAdvisoryInstitutionsLinkResponseBody}
   *   containing the link token string to use with Plaid Link.
   */
  getAdvisoryInstitutionsLinkRaw: build.mutation<
    BrokerageTypes.GetAdvisoryInstitutionsLinkResponseBody,
    BrokerageTypes.GetAdvisoryInstitutionsLinkParams
  >({
    query: ({ institution_id }) => ({
      url: `/v2/advisory/institutions/link/`,
      method: 'GET',
      params: {
        institution_id,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Sync institution financial data from providers.
   *
   * Triggers a synchronization of financial data (accounts, transactions, balances)
   * from the connected institution providers (Plaid, Tink, Fintech). This forces a
   * fresh data pull from the provider's API.
   *
   * @remarks
   * This endpoint initiates an asynchronous sync job that fetches the latest
   * financial data from the institution provider. The sync may take some time to
   * complete. You can specify connection IDs to sync only specific connections, or
   * omit them to sync all.
   *
   * **Raw endpoint:** `POST /v2/advisory/institutions/sync/`
   * @example
   *
   * ```tsx
   * const [syncInstitutions, { isLoading, data }] =
   *   rawApi.useCreateAdvisoryInstitutionsSyncRawMutation();
   *
   * const result = await syncInstitutions({
   *   client_id: 123,
   *   connection_ids: [1, 2],
   *   start_date: '2021-07-01',
   * }).unwrap();
   *
   * if (result) {
   *   console.log('Batch ID:', result.batch_id);
   *   // Poll status with getAdvisoryInstitutionsSyncBatchIdStatusRaw({ batch_id: result.batch_id })
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `client_id`: (Optional) Client ID to sync institutions for (query param)
   *   - `connection_ids`: Array of connection IDs to sync
   *   - `start_date`: (Optional) Start date for the date range (YYYY-MM-DD)
   *
   * @returns A {@link BrokerageTypes.SyncResponse} with `batch_id` and
   *   `institutions`. Use `batch_id` with
   *   {@link rawApi.endpoints.getAdvisoryInstitutionsSyncBatchIdStatusRaw} to poll
   *   sync status.
   */
  createAdvisoryInstitutionsSyncRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryInstitutionsSyncResponseBody,
    BrokerageTypes.CreateAdvisoryInstitutionsSyncParams &
      BrokerageTypes.CreateAdvisoryInstitutionsSyncRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/institutions/sync/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Get the status of a sync process (no cache).
   *
   * Returns the status of an advisory institutions sync batch started via POST
   * /v2/advisory/institutions/sync/. Exposed as a **mutation** so it is not cached;
   * use for polling after starting a sync.
   *
   * @remarks
   * **Raw endpoint:** `GET /v2/advisory/institutions/sync/{batch_id}/status/`
   * @param params - `batch_id`: Sync batch ID returned when starting the sync (POST
   *   /v2/advisory/institutions/sync/).
   * @returns {@link BrokerageTypes.SyncStatus} (batch_id, is_completed, started_at,
   *   connections).
   */
  getAdvisoryInstitutionsSyncBatchIdStatusRaw: build.mutation<
    BrokerageTypes.GetAdvisoryInstitutionsSyncBatchIdStatusResponseBody,
    BrokerageTypes.GetAdvisoryInstitutionsSyncBatchIdStatusParams
  >({
    query: ({ batch_id }) => ({
      url: `/v2/advisory/institutions/sync/${batch_id}/status/`,
      method: 'GET',
    }),
    invalidatesTags: [],
  }),

  /**
   * Delete a linked institution by its ID.
   *
   * Removes a financial institution connection and all associated account data from
   * the user's account. This permanently disconnects the institution and removes
   * all synced data.
   *
   * @remarks
   * This action is irreversible. All account data, transactions, and balances
   * associated with this institution will be removed. Use with caution.
   *
   * **Raw endpoint:** `DELETE /v2/advisory/institutions/{institution_id}/`
   * @example
   *
   * ```tsx
   * const [deleteInstitution, { isLoading, isSuccess }] =
   *   rawApi.useDeleteAdvisoryInstitutionsInstitutionIdRawMutation();
   *
   * await deleteInstitution({ institution_id: 'ins_1234567890' });
   *
   * if (isSuccess) {
   *   console.log('Institution deleted successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: ID of the institution to delete
   *
   * @returns Void response (204) upon successful deletion.
   */
  deleteAdvisoryInstitutionsInstitutionIdRaw: build.mutation<
    BrokerageTypes.DeleteAdvisoryInstitutionsInstitutionIdResponseBody,
    BrokerageTypes.DeleteAdvisoryInstitutionsInstitutionIdParams
  >({
    query: ({ institution_id }) => ({
      url: `/v2/advisory/institutions/${institution_id}/`,
      method: 'DELETE',
    }),
    invalidatesTags: [],
  }),

  /**
   * Get all advisory proposals.
   *
   * Retrieves a list of all advisory proposals associated with the authenticated
   * user. This includes proposals in various states (pending, active, signed, etc.)
   * with their contract details and client information.
   *
   * @remarks
   * This endpoint returns comprehensive proposal information including proposal ID,
   * client details, advisory type, commission information, status, contract
   * details, and signature information.
   *
   * **Raw endpoint:** `GET /v2/advisory/proposals/`
   * @example
   *
   * ```tsx
   * const { data: proposals, isLoading } =
   *   rawApi.useGetAdvisoryProposalsRawQuery();
   *
   * // Display proposals
   * proposals?.forEach(proposal => {
   *   console.log(`Proposal ID: ${proposal.id}`);
   *   console.log(`Client: ${proposal.client?.name || 'N/A'}`);
   *   console.log(`Status: ${proposal.status}`);
   *   console.log(`Advisory Type: ${proposal.advisory_type}`);
   *   if (proposal.contract) {
   *     console.log(`Contract signed: ${proposal.contract.signed}`);
   *   }
   * });
   * ```
   *
   * @returns An array of {@link BrokerageTypes.GetAdvisoryProposalsResponseBody}
   *   containing proposal information including ID, client, advisory type,
   *   commission, status, contract, and signature details.
   */
  getAdvisoryProposalsRaw: build.query<
    BrokerageTypes.GetAdvisoryProposalsResponseBody,
    void
  >({
    query: () => ({
      url: `/v2/advisory/proposals/`,
      method: 'GET',
    }),
  }),

  /**
   * Create an advisory proposal for a client.
   *
   * Creates a new advisory proposal that defines the advisory relationship between
   * a Citec advisor and a client, including advisory type, commission structure,
   * and associated institutions.
   *
   * @remarks
   * This endpoint creates a proposal that must be signed by the client before
   * becoming active. The proposal includes details about the advisory type,
   * commission on managed assets, and which institutions are included in the
   * advisory relationship.
   *
   * **Raw endpoint:** `POST /v2/advisory/proposals/`
   * @example
   *
   * ```tsx
   * const [createProposal, { isLoading, data: newProposal }] =
   *   rawApi.useCreateAdvisoryProposalsRawMutation();
   *
   * await createProposal({
   *   client_id: 123,
   *   ibans: ['ES1234567890123456789012', 'INVERSIS-CUSTODY'],
   *   advisory_type: 'discretionary',
   *   commission_managed_assets: 0.01, // 1% commission
   * });
   *
   * if (newProposal) {
   *   console.log(`Proposal created: ${newProposal.id}`);
   *   console.log(`Status: ${newProposal.status}`);
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `client_id`: ID of the client for this proposal
   *   - `ibans`: Array of account IBANs to include in the proposal
   *   - `advisory_type`: Type of advisory service ('discretionary',
   *       'non-discretionary', etc.)
   *   - `commission_managed_assets`: Commission rate on managed assets (decimal, e.g.,
   *       0.01 for 1%)
   *
   * @returns A {@link BrokerageTypes.CreateAdvisoryProposalsResponseBody} containing
   *   the newly created proposal information including ID, status, and contract
   *   details.
   */
  createAdvisoryProposalsRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryProposalsResponseBody,
    BrokerageTypes.CreateAdvisoryProposalsRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/proposals/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Simulate a target portfolio from current positions and operations.
   *
   * **Raw endpoint:** `POST /v2/advisory/proposals/target-portfolio/`
   *
   * @returns {@link BrokerageTypes.CreateAdvisoryProposalsTargetPortfolioResponseBody}
   */
  createAdvisoryProposalsTargetPortfolioRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryProposalsTargetPortfolioResponseBody,
    BrokerageTypes.CreateAdvisoryProposalsTargetPortfolioRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/proposals/target-portfolio/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Update an advisory contract for a proposal.
   *
   * Updates the contract details for an existing advisory proposal, including
   * contract terms, commission structure, and other contractual information.
   *
   * @remarks
   * This endpoint allows modification of contract details for a proposal. The
   * contract must typically be unsigned or in a draft state to be updated. Once
   * signed, contract modifications may require creating a new proposal.
   *
   * **Raw endpoint:** `POST /v2/advisory/proposals/contracts/`
   * @example
   *
   * ```tsx
   * const [updateContract, { isLoading, isSuccess }] =
   *   rawApi.useCreateAdvisoryProposalsContractsRawMutation();
   *
   * await updateContract({
   *   proposal_id: 456,
   *   commission_managed_assets: 0.015, // Update to 1.5%
   *   contract_terms: 'Updated terms...',
   * });
   *
   * if (isSuccess) {
   *   console.log('Contract updated successfully');
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `proposal_id`: ID of the proposal to update the contract for
   *   - `commission_managed_assets`: (Optional) Updated commission rate
   *   - `contract_terms`: (Optional) Updated contract terms
   *   - Other contract-related fields
   *
   * @returns Updated contract information.
   */
  createAdvisoryProposalsContractsRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryProposalsContractsResponseBody,
    BrokerageTypes.CreateAdvisoryProposalsContractsRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/proposals/contracts/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Delete an advisory proposal.
   *
   * Permanently removes an advisory proposal. This action is typically only allowed
   * for proposals that are in draft or pending status, not for signed or active
   * proposals.
   *
   * @remarks
   * This action is irreversible. Deleting a proposal will remove all associated
   * contract information and proposal history. Use with caution.
   *
   * **Raw endpoint:** `DELETE /v2/advisory/proposals/{proposal_id}/`
   * @example
   *
   * ```tsx
   * const [deleteProposal, { isLoading, isSuccess }] =
   *   rawApi.useDeleteAdvisoryProposalsProposalIdRawMutation();
   *
   * await deleteProposal({ proposal_id: 456 });
   *
   * if (isSuccess) {
   *   console.log('Proposal deleted successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `proposal_id`: ID of the proposal to delete
   *
   * @returns Void response (204) upon successful deletion.
   */
  deleteAdvisoryProposalsProposalIdRaw: build.mutation<
    BrokerageTypes.DeleteAdvisoryProposalsProposalIdResponseBody,
    BrokerageTypes.DeleteAdvisoryProposalsProposalIdParams
  >({
    query: ({ proposal_id }) => ({
      url: `/v2/advisory/proposals/${proposal_id}/`,
      method: 'DELETE',
    }),
    invalidatesTags: [],
  }),

  /**
   * Update the status of an advisory proposal.
   *
   * Updates the status or other properties of an existing advisory proposal. This
   * can be used to change the proposal status, update commission rates, or modify
   * other proposal attributes.
   *
   * @remarks
   * This endpoint allows partial updates to a proposal. Only the fields provided in
   * the request body will be updated. Status changes may be restricted based on the
   * current state of the proposal (e.g., signed proposals may not be modifiable).
   *
   * **Raw endpoint:** `PATCH /v2/advisory/proposals/{proposal_id}/`
   * @example
   *
   * ```tsx
   * const [updateProposal, { isLoading, isSuccess }] =
   *   rawApi.useUpdateAdvisoryProposalsProposalIdRawMutation();
   *
   * await updateProposal({
   *   proposal_id: 456,
   *   status: 'active',
   *   commission_managed_assets: 0.012,
   * });
   *
   * if (isSuccess) {
   *   console.log('Proposal updated successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `proposal_id`: ID of the proposal to update
   *   - `status`: (Optional) New status for the proposal
   *   - `commission_managed_assets`: (Optional) Updated commission rate
   *   - Other proposal fields to update
   *
   * @returns Updated proposal information.
   */
  updateAdvisoryProposalsProposalIdRaw: build.mutation<
    BrokerageTypes.UpdateAdvisoryProposalsProposalIdResponseBody,
    BrokerageTypes.UpdateAdvisoryProposalsProposalIdParams &
      BrokerageTypes.UpdateAdvisoryProposalsProposalIdRequestBody
  >({
    query: ({ proposal_id, ...body }) => ({
      url: `/v2/advisory/proposals/${proposal_id}/`,
      method: 'PATCH',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Sign an advisory proposal.
   *
   * Marks an advisory proposal as signed, activating the advisory relationship
   * between the advisor and client. Once signed, the proposal becomes active and
   * the advisory services can begin.
   *
   * @remarks
   * Signing a proposal is typically the final step in the proposal workflow. Once
   * signed, the proposal status changes to 'active' and the contract terms become
   * binding. The proposal must be in a 'pending' or 'draft' state to be signed.
   *
   * **Raw endpoint:** `POST /v2/advisory/proposals/{proposal_id}/sign/`
   * @example
   *
   * ```tsx
   * const [signProposal, { isLoading, isSuccess }] =
   *   rawApi.useCreateAdvisoryProposalsProposalIdSignRawMutation();
   *
   * await signProposal({ proposal_id: 456 });
   *
   * if (isSuccess) {
   *   console.log('Proposal signed successfully');
   *   console.log('Advisory relationship is now active');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `proposal_id`: ID of the proposal to sign
   *
   * @returns Void response (204) upon successful signing.
   */
  createAdvisoryProposalsProposalIdSignRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryProposalsProposalIdSignResponseBody,
    BrokerageTypes.CreateAdvisoryProposalsProposalIdSignParams
  >({
    query: ({ proposal_id }) => ({
      url: `/v2/advisory/proposals/${proposal_id}/sign/`,
      method: 'POST',
    }),
    invalidatesTags: [],
  }),

  /**
   * Get advisors with their shared institutions for retail user.
   *
   * Retrieves a list of advisors who have shared institutions with the current
   * retail user. This is used to display which advisors have access to the user's
   * financial institution data.
   *
   * @remarks
   * This endpoint is used in the retail user context to see which advisors have
   * been granted access to view their financial institution data. It returns
   * advisor information along with the institutions that are shared with each
   * advisor.
   *
   * **Raw endpoint:** `GET /v2/advisory/users/`
   * @example
   *
   * ```tsx
   * const { data: advisors, isLoading } = rawApi.useGetAdvisoryUsersRawQuery();
   *
   * // Display advisors with shared institutions
   * advisors?.forEach(advisor => {
   *   console.log(`Advisor: ${advisor.name || advisor.email}`);
   *   console.log(`Shared institutions: ${advisor.institutions?.length || 0}`);
   *   advisor.institutions?.forEach(inst => {
   *     console.log(`  - ${inst.name}`);
   *   });
   * });
   * ```
   *
   * @returns An array of {@link BrokerageTypes.GetAdvisoryUsersResponseBody}
   *   containing advisor information including name, email, and shared
   *   institutions.
   */
  getAdvisoryUsersRaw: build.query<
    BrokerageTypes.GetAdvisoryUsersResponseBody,
    void
  >({
    query: () => ({
      url: `/v2/advisory/users/`,
      method: 'GET',
    }),
  }),

  /**
   * Share accounts with users.
   *
   * Shares specific accounts with another user (typically an advisor) by providing
   * their email address and the IBANs of the accounts to share.
   *
   * @remarks
   * This endpoint establishes a sharing relationship for specific accounts. The
   * recipient user will be able to view data from the shared accounts once they
   * confirm the sharing request.
   *
   * **Raw endpoint:** `POST /v2/advisory/users/accounts/`
   * @example
   *
   * ```tsx
   * const [shareAccounts, { isLoading, isSuccess }] =
   *   rawApi.useCreateAdvisoryUsersAccountsRawMutation();
   *
   * await shareAccounts({
   *   user_email: 'advisor@example.com',
   *   ibans: ['ES1234567890123456789012'],
   * });
   *
   * if (isSuccess) {
   *   console.log('Accounts shared successfully');
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `user_email`: Email of the user to share accounts with
   *   - `ibans`: (Optional) Array of IBANs to share
   *
   * @returns Response with sharing confirmation data.
   */
  createAdvisoryUsersAccountsRaw: build.mutation<
    BrokerageTypes.CreateAdvisoryUsersAccountsResponseBody,
    BrokerageTypes.CreateAdvisoryUsersAccountsRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/users/accounts/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Confirm or reject shared accounts.
   *
   * Confirms or rejects a sharing request for specific accounts. This endpoint is
   * used by the recipient user to accept or decline account sharing requests.
   *
   * @remarks
   * This endpoint allows the recipient user to confirm or reject account sharing
   * requests. When confirmed, the user gains access to view data from the shared
   * accounts. When rejected, the sharing request is declined.
   *
   * **Raw endpoint:** `PATCH /v2/advisory/users/accounts/`
   * @example
   *
   * ```tsx
   * const [confirmAccounts, { isLoading, isSuccess }] =
   *   rawApi.useUpdateAdvisoryUsersAccountsRawMutation();
   *
   * await confirmAccounts({
   *   ibans: ['ES1234567890123456789012'],
   *   confirm: true,
   * });
   *
   * if (isSuccess) {
   *   console.log('Accounts confirmed successfully');
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `ibans`: (Optional) Array of IBANs to confirm or reject
   *   - `confirm`: Boolean indicating whether to confirm (true) or reject (false)
   *
   * @returns Void response (204) upon successful update.
   */
  updateAdvisoryUsersAccountsRaw: build.mutation<
    BrokerageTypes.UpdateAdvisoryUsersAccountsResponseBody,
    BrokerageTypes.UpdateAdvisoryUsersAccountsRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/users/accounts/`,
      method: 'PATCH',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Unshare shared accounts.
   *
   * Revokes access to specific accounts that were previously shared with an
   * advisor. This removes the advisor's ability to view data from the specified
   * accounts while maintaining access to other shared accounts.
   *
   * @remarks
   * This endpoint allows granular control over account sharing. You can unshare
   * specific accounts by providing their IBANs, while leaving other accounts
   * shared. This is useful when a client wants to revoke access to specific
   * accounts without removing the advisor relationship entirely.
   *
   * **Raw endpoint:** `PATCH /v2/advisory/users/accounts/unshare/`
   * @example
   *
   * ```tsx
   * const [unshareAccounts, { isLoading, isSuccess }] =
   *   rawApi.useUnshareAdvisoryUsersAccountsRawMutation();
   *
   * await unshareAccounts({
   *   client_id: 123,
   *   ibans: ['ES1234567890123456789012'],
   *   user_id: 456,
   * });
   *
   * if (isSuccess) {
   *   console.log('Accounts unshared successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `client_id`: (Optional) Client ID for the account sharing context
   *   - `ibans`: Array of IBANs to unshare
   *   - `user_id`: ID of the user to unshare accounts from
   *
   * @returns Void response (204) upon successful update.
   */
  unshareAdvisoryUsersAccountsRaw: build.mutation<
    BrokerageTypes.UnshareAdvisoryUsersAccountsResponseBody,
    BrokerageTypes.UnshareAdvisoryUsersAccountsParams &
      BrokerageTypes.UnshareAdvisoryUsersAccountsRequestBody
  >({
    query: ({ client_id, ...body }) => ({
      url: `/v2/advisory/users/accounts/unshare/`,
      method: 'PATCH',
      body,
      params: {
        client_id,
      },
    }),
    invalidatesTags: [],
  }),
});

const getAggregatorEndpoints = (build: EndpointBuilders) => ({
  /**
   * Create a link token for the Plaid Link flow.
   *
   * Generates a link token that can be used to initialize the Plaid Link component
   * for connecting a financial institution in the aggregator context. This is
   * similar to the advisory link token endpoint but scoped to aggregator
   * operations.
   *
   * @remarks
   * This endpoint generates a temporary link token for use with the Plaid Link SDK.
   * The token is short-lived and should be used immediately. An optional
   * `institution_id` can be provided to pre-select a specific institution or to
   * update an existing institution connection.
   *
   * **Raw endpoint:** `GET /v2/aggregator/link/create/`
   * @example
   *
   * ```tsx
   * const { data: linkTokenData, isLoading } =
   *   rawApi.useGetAggregatorLinkCreateRawQuery({
   *     institution_id: 'ins_1234567890', // Optional, for updating existing
   *   });
   *
   * // Use link token with Plaid Link
   * if (linkTokenData) {
   *   const { link_token } = linkTokenData;
   *   // Initialize Plaid Link: plaidLink.open({ token: link_token });
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: (Optional) Institution ID to associate with the link token
   *       (for updating existing connections)
   *
   * @returns A {@link BrokerageTypes.GetAggregatorLinkCreateResponseBody} containing
   *   the link token string to use with Plaid Link.
   */
  getAggregatorLinkCreateRaw: build.query<
    BrokerageTypes.GetAggregatorLinkCreateResponseBody,
    BrokerageTypes.GetAggregatorLinkCreateParams
  >({
    query: ({ institution_id }) => ({
      url: `/v2/aggregator/link/create/`,
      method: 'GET',
      params: {
        institution_id,
      },
    }),
  }),

  /**
   * Get investment proposals for a client.
   *
   * **Raw endpoint:** `GET /v2/advisory/investment-proposals/`
   */
  getInvestmentProposalsRaw: build.query<
    BrokerageTypes.GetInvestmentProposalsResponseBody,
    BrokerageTypes.GetInvestmentProposalsParams | void
  >({
    query: params => ({
      url: `/v2/advisory/investment-proposals/`,
      method: 'GET',
      params: {
        client_id: params?.client_id,
      },
    }),
  }),

  /**
   * Create a proposal for a client.
   *
   * Creates a new trading proposal containing trading operations, current portfolio
   * positions, target portfolio, and risk/return analysis. This proposal can be
   * reviewed by the client before execution.
   *
   * @remarks
   * This endpoint creates a comprehensive trading proposal that includes:
   *
   * - Trading operations (buy/sell orders)
   * - Current portfolio state
   * - Target portfolio after operations
   * - Risk and return analysis
   * - Currency and risk profile information
   *
   * The proposal is created in a draft state and can be updated before being
   * submitted for client review.
   *
   * **Raw endpoint:** `POST /v2/advisory/investment-proposals/`
   * @example
   *
   * ```tsx
   * const [createProposal, { isLoading, data: newProposal }] =
   *   rawApi.useCreateAggregatorProposalsRawMutation();
   *
   * await createProposal({
   *   client_id: 123,
   *   name: 'Q4 2024 Rebalancing',
   *   description: 'Rebalancing portfolio for Q4',
   *   currency: 'USD',
   *   operations: [
   *     { ticker: 'AAPL', quantity: 10, action: 'buy', institution_id: 'ins_123' },
   *     { ticker: 'GOOGL', quantity: 5, action: 'sell', institution_id: 'ins_123' },
   *   ],
   *   current_portfolio: [...],
   *   proposal: [...],
   *   returns: {...},
   *   risk_returns: [...],
   * });
   *
   * if (newProposal) {
   *   console.log(`Proposal created: ${newProposal.id}`);
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `client_id`: Client ID for this proposal
   *   - `name`: Proposal name
   *   - `description`: (Optional) Proposal description
   *   - `currency`: Currency for the proposal ('USD' or 'EUR')
   *   - `operations`: Array of trading operations
   *   - `current_portfolio`: Current portfolio positions
   *   - `proposal`: Target portfolio positions
   *   - `returns`: Return analysis data
   *   - `risk_returns`: Risk/return analysis array
   *   - `risk_profile`: (Optional) Risk profile identifier
   *
   * @returns A {@link BrokerageTypes.CreateAggregatorProposalsResponseBody}
   *   containing the newly created proposal information.
   */
  createAggregatorProposalsRaw: build.mutation<
    BrokerageTypes.CreateAggregatorProposalsResponseBody,
    BrokerageTypes.CreateAggregatorProposalsParams &
      BrokerageTypes.CreateAggregatorProposalsRequestBody
  >({
    query: ({ client_id, ...body }) => ({
      url: `/v2/advisory/investment-proposals/`,
      method: 'POST',
      body,
      params: {
        client_id,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Delete a sent investment proposal.
   *
   * **Raw endpoint:** `DELETE /v2/advisory/investment-proposals/{proposal_id}/`
   */
  deleteInvestmentProposalRaw: build.mutation<
    BrokerageTypes.DeleteInvestmentProposalResponseBody,
    BrokerageTypes.DeleteInvestmentProposalParams
  >({
    query: ({ proposal_id, client_id }) => ({
      url: `/v2/advisory/investment-proposals/${proposal_id}/`,
      method: 'DELETE',
      params: {
        client_id,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Create a target portfolio from a proposal and trades.
   *
   * Calculates and returns the target portfolio that would result from executing a
   * set of trading operations on the current portfolio. This is used to preview
   * portfolio changes before executing trades.
   *
   * @remarks
   * This endpoint performs portfolio simulation by applying trading operations to
   * the current portfolio positions. It returns the resulting target portfolio with
   * updated positions, quantities, and values. This is useful for visualizing
   * portfolio changes and validating trading strategies before execution.
   *
   * **Raw endpoint:** `POST /v2/advisory/proposals/target-portfolio/`
   * @example
   *
   * ```tsx
   * const [createTargetPortfolio, { isLoading, data: targetPortfolio }] =
   *   rawApi.useCreateAggregatorProposalsTargetPortfolioRawMutation();
   *
   * await createTargetPortfolio({
   *   current_portfolio: [
   *     { ticker: 'AAPL', quantity: 100, iban: 'ES12', amount: 15000 },
   *     {
   *       ticker: '$CASH',
   *       quantity: 1,
   *       iban: 'ES12',
   *       amount: 1000,
   *       isin: '$CASH',
   *     },
   *   ],
   *   operations: [
   *     {
   *       operation_type: 'buy',
   *       from_iban: 'ES12',
   *       from_ticker: '$CASH',
   *       from_isin: '$CASH',
   *       to_iban: 'ES12',
   *       to_ticker: 'AAPL',
   *       quantity: 10,
   *       to_price: 150,
   *       amount: 1500,
   *     },
   *   ],
   * });
   *
   * if (targetPortfolio) {
   *   targetPortfolio.positions.forEach(position => {
   *     console.log(
   *       `${position.ticker}: ${position.quantity} @ ${position.amount}`,
   *     );
   *   });
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `current_portfolio`: Array of current portfolio positions
   *   - `operations`: Array of trading operations to apply
   *
   * @returns A
   *   {@link BrokerageTypes.CreateAggregatorProposalsTargetPortfolioResponseBody}
   *   envelope with `status`, `positions`, and `operation_results`.
   */
  createAggregatorProposalsTargetPortfolioRaw: build.mutation<
    BrokerageTypes.CreateAggregatorProposalsTargetPortfolioResponseBody,
    BrokerageTypes.CreateAggregatorProposalsTargetPortfolioRequestBody
  >({
    query: body => ({
      url: `/v2/advisory/proposals/target-portfolio/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * List advisors for a given user.
   *
   * Retrieves a list of advisors associated with the authenticated user in the
   * aggregator context. This is used to display which advisors have access to the
   * user's data or to manage advisor relationships.
   *
   * @remarks
   * This endpoint returns advisor information including their user details, shared
   * institutions, and relationship status. Use this to populate advisor selection
   * lists or to display advisor access information.
   *
   * **Raw endpoint:** `GET /v2/aggregator/users/`
   * @example
   *
   * ```tsx
   * const { data: advisors, isLoading } =
   *   rawApi.useGetAggregatorUsersRawQuery();
   *
   * // Display advisors
   * advisors?.forEach(advisor => {
   *   console.log(`Advisor: ${advisor.name || advisor.email}`);
   *   console.log(`User ID: ${advisor.user_id}`);
   *   console.log(`Shared institutions: ${advisor.institutions?.length || 0}`);
   * });
   * ```
   *
   * @returns An array of {@link BrokerageTypes.GetAggregatorUsersResponseBody}
   *   containing advisor information including user_id, name, email, and shared
   *   institutions.
   */
  getAggregatorUsersRaw: build.query<
    BrokerageTypes.GetAggregatorUsersResponseBody,
    void
  >({
    query: () => ({
      url: `/v2/aggregator/users/`,
      method: 'GET',
    }),
  }),
});

const getAggregatorInstitutionsEndpoints = (build: EndpointBuilders) => ({
  /**
   * Get all available linked institutions.
   *
   * Retrieves a list of all financial institutions that are connected to the
   * aggregator for the authenticated user or a specific client. This includes
   * institutions from various providers (Plaid, Tink, Fintech) with their
   * connection status and metadata.
   *
   * @remarks
   * This endpoint returns institution information including provider type,
   * connection status, last sync timestamp, and available products. The `details`
   * parameter can be used to include additional detailed information about each
   * institution.
   *
   * **Raw endpoint:** `GET /v2/aggregator/institutions/`
   * @example
   *
   * ```tsx
   * const { data: institutions, isLoading } =
   *   rawApi.useGetAggregatorInstitutionsRawQuery({
   *     client_id: 123, // Optional
   *     details: true, // Optional, include detailed information
   *   });
   *
   * // Display institutions
   * institutions?.forEach(institution => {
   *   console.log(`Institution: ${institution.name}`);
   *   console.log(`Provider: ${institution.provider}`);
   *   console.log(`Status: ${institution.status}`);
   *   console.log(`Last sync: ${institution.last_sync || 'Never'}`);
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `client_id`: (Optional) Client ID to filter institutions
   *   - `details`: (Optional) Whether to include detailed institution information
   *
   * @returns An array of
   *   {@link BrokerageTypes.GetAggregatorInstitutionsResponseBody} containing
   *   institution information including ID, name, provider, status, logo URL, last
   *   sync, and available products.
   */
  getAggregatorInstitutionsRaw: build.query<
    BrokerageTypes.GetAggregatorInstitutionsResponseBody,
    BrokerageTypes.GetAggregatorInstitutionsParams
  >({
    query: ({ details, client_id }) => ({
      url: `/v2/aggregator/institutions/`,
      method: 'GET',
      params: {
        details,
        client_id,
      },
    }),
  }),

  /**
   * Create an institution by exchanging a Plaid public token for an access token.
   *
   * Establishes a connection to a financial institution by exchanging provider
   * credentials (e.g., Plaid public token) for an access token and creating the
   * institution record in the aggregator context.
   *
   * @remarks
   * This endpoint is used during the institution linking flow in the aggregator. It
   * accepts provider-specific credentials (public token for Plaid, authorization
   * code for Tink, etc.) and creates the institution connection. The provider type
   * must match the credentials format. This is similar to the advisory institution
   * creation but scoped to aggregator operations.
   *
   * **Raw endpoint:** `POST /v2/aggregator/institutions/`
   * @example
   *
   * ```tsx
   * const [createInstitution, { isLoading, data: newInstitution }] =
   *   rawApi.useCreateAggregatorInstitutionsRawMutation();
   *
   * await createInstitution({
   *   public_token: 'public-sandbox-abc123',
   *   provider: 'plaid',
   *   code_verifier: 'optional-verifier-for-pkce', // Optional
   * });
   *
   * if (newInstitution) {
   *   console.log(`Institution created: ${newInstitution.name}`);
   *   console.log(`Status: ${newInstitution.status}`);
   *   console.log(`Institution ID: ${newInstitution.id}`);
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `public_token`: Public token from the provider (Plaid, Tink, etc.)
   *   - `code_verifier`: (Optional) OAuth code verifier for PKCE flow
   *   - `provider`: Provider type ('plaid', 'tink', or 'fintech')
   *
   * @returns A {@link BrokerageTypes.CreateAggregatorInstitutionsResponseBody}
   *   containing the newly created institution information including ID, name,
   *   status, provider, and logo URL.
   */
  createAggregatorInstitutionsRaw: build.mutation<
    BrokerageTypes.CreateAggregatorInstitutionsResponseBody,
    BrokerageTypes.CreateAggregatorInstitutionsRequestBody
  >({
    query: body => ({
      url: `/v2/aggregator/institutions/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Get cashflow for all linked institutions.
   *
   * Retrieves cashflow data (inflows and outflows) across all connected financial
   * institutions for a specified date range. This aggregates transaction data to
   * show money movement patterns.
   *
   * @remarks
   * This endpoint calculates cashflow by aggregating transactions from all
   * connected institutions. It returns inflows (deposits, credits) and outflows
   * (withdrawals, debits) for the specified period. Use this for cashflow analysis,
   * budgeting, or financial planning.
   *
   * **Raw endpoint:** `GET /v2/aggregator/institutions/cashflow/`
   * @example
   *
   * ```tsx
   * const { data: cashflow, isLoading } =
   *   rawApi.useGetAggregatorInstitutionsCashflowRawQuery({
   *     start_date: '2024-01-01',
   *     end_date: '2024-12-31',
   *     client_id: 123, // Optional
   *   });
   *
   * // Analyze cashflow
   * if (cashflow) {
   *   console.log(`Total inflows: ${cashflow.total_inflows}`);
   *   console.log(`Total outflows: ${cashflow.total_outflows}`);
   *   console.log(`Net cashflow: ${cashflow.net_cashflow}`);
   *   cashflow.by_institution?.forEach(inst => {
   *     console.log(`${inst.institution_id}: ${inst.net_cashflow}`);
   *   });
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `start_date`: Start date for cashflow data (ISO 8601 format: YYYY-MM-DD)
   *   - `end_date`: End date for cashflow data (ISO 8601 format: YYYY-MM-DD)
   *   - `client_id`: (Optional) Client ID to filter data
   *
   * @returns Cashflow data including total inflows, outflows, net cashflow, and
   *   breakdown by institution.
   */
  getAggregatorInstitutionsCashflowRaw: build.query<
    BrokerageTypes.GetAggregatorInstitutionsCashflowResponseBody,
    BrokerageTypes.GetAggregatorInstitutionsCashflowParams
  >({
    query: ({ start_date, end_date, client_id }) => ({
      url: `/v2/aggregator/institutions/cashflow/`,
      method: 'GET',
      params: {
        start_date,
        end_date,
        client_id,
      },
    }),
  }),

  /**
   * Get portfolio returns for a client.
   *
   * Calculates and returns portfolio performance metrics including total returns,
   * period returns, and return breakdowns by institution or asset class. This
   * provides comprehensive return analysis for portfolio evaluation.
   *
   * @remarks
   * This endpoint performs return calculations based on portfolio positions and
   * historical price data. It can calculate returns for specific date ranges,
   * compare performance across institutions, and provide detailed return
   * breakdowns. Use this for performance reporting, benchmarking, or portfolio
   * analysis.
   *
   * **Raw endpoint:** `POST /v2/aggregator/institutions/positions/returns/`
   * @example
   *
   * ```tsx
   * const [getReturns, { isLoading, data: returns }] =
   *   rawApi.useCreateAggregatorInstitutionsPositionsReturnsRawMutation();
   *
   * await getReturns({
   *   client_id: 123,
   *   start_date: '2024-01-01',
   *   end_date: '2024-12-31',
   *   positions: [...], // Portfolio positions
   * });
   *
   * // Analyze returns
   * if (returns) {
   *   console.log(`Total return: ${returns.total_return}%`);
   *   console.log(`Period return: ${returns.period_return}%`);
   *   returns.by_institution?.forEach(inst => {
   *     console.log(`${inst.institution_id}: ${inst.return}%`);
   *   });
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `client_id`: (Optional) Client ID to calculate returns for
   *   - `start_date`: Start date for return calculation (ISO 8601 format)
   *   - `end_date`: End date for return calculation (ISO 8601 format)
   *   - `positions`: (Optional) Specific positions to calculate returns for
   *
   * @returns Return analysis data including total returns, period returns, and
   *   breakdowns by institution or asset class.
   */
  createAggregatorInstitutionsPositionsReturnsRaw: build.mutation<
    BrokerageTypes.CreateAggregatorInstitutionsPositionsReturnsResponseBody,
    BrokerageTypes.CreateAggregatorInstitutionsPositionsReturnsRequestBody
  >({
    query: body => ({
      url: `/v2/aggregator/institutions/positions/returns/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),

  /**
   * Delete an institution by its institution ID.
   *
   * Permanently removes a financial institution connection and all associated
   * account data, transactions, and positions from the aggregator. This action is
   * irreversible.
   *
   * @remarks
   * This action permanently deletes the institution connection and all associated
   * data including accounts, transactions, positions, and balances. Use with
   * caution as this cannot be undone. To temporarily disable an institution without
   * deleting data, consider updating its status instead.
   *
   * **Raw endpoint:** `DELETE /v2/aggregator/institutions/{institution_id}/`
   * @example
   *
   * ```tsx
   * const [deleteInstitution, { isLoading, isSuccess }] =
   *   rawApi.useDeleteAggregatorInstitutionsInstitutionIdRawMutation();
   *
   * await deleteInstitution({ institution_id: 'ins_1234567890' });
   *
   * if (isSuccess) {
   *   console.log('Institution deleted successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: ID of the institution to delete
   *
   * @returns Void response (204) upon successful deletion.
   */
  deleteAggregatorInstitutionsInstitutionIdRaw: build.mutation<
    BrokerageTypes.DeleteAggregatorInstitutionsInstitutionIdResponseBody,
    BrokerageTypes.DeleteAggregatorInstitutionsInstitutionIdParams
  >({
    query: ({ institution_id }) => ({
      url: `/v2/aggregator/institutions/${institution_id}/`,
      method: 'DELETE',
    }),
    invalidatesTags: [],
  }),

  /**
   * Get cashflow for a linked institution.
   *
   * Retrieves cashflow data (inflows and outflows) for a specific financial
   * institution over a specified date range. This shows money movement patterns for
   * a single institution.
   *
   * @remarks
   * This endpoint calculates cashflow for a single institution by aggregating
   * transactions. It returns inflows (deposits, credits) and outflows (withdrawals,
   * debits) for the specified period. Use this for institution- specific cashflow
   * analysis or to compare cashflow patterns across institutions.
   *
   * **Raw endpoint:** `GET /v2/aggregator/institutions/{institution_id}/cashflow/`
   * @example
   *
   * ```tsx
   * const { data: cashflow, isLoading } =
   *   rawApi.useGetAggregatorInstitutionsInstitutionIdCashflowRawQuery({
   *     institution_id: 'ins_1234567890',
   *     start_date: '2024-01-01',
   *     end_date: '2024-12-31',
   *     client_id: 123, // Optional
   *   });
   *
   * // Analyze institution cashflow
   * if (cashflow) {
   *   console.log(`Inflows: ${cashflow.total_inflows}`);
   *   console.log(`Outflows: ${cashflow.total_outflows}`);
   *   console.log(`Net: ${cashflow.net_cashflow}`);
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: ID of the institution to get cashflow for
   *   - `start_date`: Start date for cashflow data (ISO 8601 format: YYYY-MM-DD)
   *   - `end_date`: End date for cashflow data (ISO 8601 format: YYYY-MM-DD)
   *   - `client_id`: (Optional) Client ID to filter data
   *
   * @returns Cashflow data for the institution including total inflows, outflows,
   *   net cashflow, and breakdown by account if available.
   */
  getAggregatorInstitutionsInstitutionIdCashflowRaw: build.query<
    BrokerageTypes.GetAggregatorInstitutionsInstitutionIdCashflowResponseBody,
    BrokerageTypes.GetAggregatorInstitutionsInstitutionIdCashflowParams
  >({
    query: ({ institution_id, start_date, end_date, client_id }) => ({
      url: `/v2/aggregator/institutions/${institution_id}/cashflow/`,
      method: 'GET',
      params: {
        start_date,
        end_date,
        client_id,
      },
    }),
  }),

  /**
   * Delete institution positions by their IDs.
   *
   * Removes specific portfolio positions from an institution by providing their
   * position IDs. This is useful for cleaning up duplicate positions, removing
   * manually added positions, or correcting position data.
   *
   * @remarks
   * This endpoint allows selective deletion of positions by providing a list of
   * position IDs. Only the specified positions will be removed; other positions
   * remain unchanged. Use this to clean up position data or remove incorrect
   * entries.
   *
   * **Raw endpoint:** `DELETE
   * /v2/aggregator/institutions/{institution_id}/positions/`
   * @example
   *
   * ```tsx
   * const [deletePositions, { isLoading, isSuccess }] =
   *   rawApi.useDeleteAggregatorInstitutionsInstitutionIdPositionsRawMutation();
   *
   * await deletePositions({
   *   institution_id: 'ins_1234567890',
   *   ids: ['pos_123', 'pos_456', 'pos_789'],
   *   client_id: 123, // Optional
   * });
   *
   * if (isSuccess) {
   *   console.log('Positions deleted successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: ID of the institution
   *   - `ids`: Array of position IDs to delete
   *   - `client_id`: (Optional) Client ID for context
   *
   * @returns Void response (204) upon successful deletion.
   */
  deleteAggregatorInstitutionsInstitutionIdPositionsRaw: build.mutation<
    BrokerageTypes.DeleteAggregatorInstitutionsInstitutionIdPositionsResponseBody,
    BrokerageTypes.DeleteAggregatorInstitutionsInstitutionIdPositionsParams
  >({
    query: ({ institution_id, ids, client_id }) => ({
      url: `/v2/aggregator/institutions/${institution_id}/positions/`,
      method: 'DELETE',
      params: {
        ids,
        client_id,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Add or update institution positions.
   *
   * Creates new positions or updates existing positions for a specific institution.
   * This is useful for manually adding positions that aren't synced from the
   * provider, correcting position data, or maintaining custom position records.
   *
   * @remarks
   * This endpoint allows manual management of positions. If a position with the
   * same identifier already exists, it will be updated; otherwise, a new position
   * will be created. Use this to maintain accurate position data when provider sync
   * doesn't capture all positions or when manual corrections are needed.
   *
   * **Raw endpoint:** `POST
   * /v2/aggregator/institutions/{institution_id}/positions/`
   * @example
   *
   * ```tsx
   * const [addOrUpdatePositions, { isLoading, isSuccess }] =
   *   rawApi.useCreateAggregatorInstitutionsInstitutionIdPositionsRawMutation();
   *
   * await addOrUpdatePositions({
   *   institution_id: 'ins_1234567890',
   *   client_id: 123, // Optional
   *   positions: [
   *     {
   *       ticker: 'AAPL',
   *       quantity: 100,
   *       price: 150.5,
   *       currency: 'USD',
   *       account_id: 'acc_123',
   *     },
   *     {
   *       ticker: 'GOOGL',
   *       quantity: 50,
   *       price: 2500.0,
   *       currency: 'USD',
   *       account_id: 'acc_123',
   *     },
   *   ],
   * });
   *
   * if (isSuccess) {
   *   console.log('Positions added/updated successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: ID of the institution
   *   - `client_id`: (Optional) Client ID for context
   *   - `positions`: Array of position objects to add or update
   *
   * @returns Created or updated position information.
   */
  createAggregatorInstitutionsInstitutionIdPositionsRaw: build.mutation<
    BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdPositionsResponseBody,
    BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdPositionsParams &
      BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdPositionsRequestBody
  >({
    query: ({ institution_id, client_id, ...body }) => ({
      url: `/v2/aggregator/institutions/${institution_id}/positions/`,
      method: 'POST',
      body,
      params: {
        client_id,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Delete manual transactions by their IDs.
   *
   * Removes specific transactions from an institution by providing their
   * transaction IDs. This is useful for cleaning up duplicate transactions,
   * removing manually added transactions, or correcting transaction data.
   *
   * @remarks
   * This endpoint allows selective deletion of transactions by providing a list of
   * transaction IDs (comma-separated string or array). Only the specified
   * transactions will be removed; other transactions remain unchanged. Use this to
   * clean up transaction data or remove incorrect entries.
   *
   * **Raw endpoint:** `DELETE
   * /v2/aggregator/institutions/{institution_id}/transactions/`
   * @example
   *
   * ```tsx
   * const [deleteTransactions, { isLoading, isSuccess }] =
   *   rawApi.useDeleteAggregatorInstitutionsInstitutionIdTransactionsRawMutation();
   *
   * await deleteTransactions({
   *   institution_id: 'ins_1234567890',
   *   transaction_ids: 'txn_123,txn_456,txn_789', // Comma-separated string
   *   // or as array: ['txn_123', 'txn_456', 'txn_789']
   *   client_id: 123, // Optional
   * });
   *
   * if (isSuccess) {
   *   console.log('Transactions deleted successfully');
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: ID of the institution
   *   - `transaction_ids`: Comma-separated string or array of transaction IDs to delete
   *   - `client_id`: (Optional) Client ID for context
   *
   * @returns Void response (204) upon successful deletion.
   */
  deleteAggregatorInstitutionsInstitutionIdTransactionsRaw: build.mutation<
    BrokerageTypes.DeleteAggregatorInstitutionsInstitutionIdTransactionsResponseBody,
    BrokerageTypes.DeleteAggregatorInstitutionsInstitutionIdTransactionsParams
  >({
    query: ({ institution_id, transaction_ids, client_id }) => ({
      url: `/v2/aggregator/institutions/${institution_id}/transactions/`,
      method: 'DELETE',
      params: {
        transaction_ids,
        client_id,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Get transactions for a linked institution.
   *
   * Retrieves transactions for a single aggregator institution within a date range.
   * The API may return an {@link BrokerageTypes.InProgressResponse} when data is
   * being refreshed; use the callback or polling pattern to obtain final results
   * when applicable.
   *
   * @remarks
   * Query params include start_date, end_date (required), and optional refresh and
   * client_id. institution_id is passed in the path.
   *
   * **Raw endpoint:** `GET
   * /v2/aggregator/institutions/{institution_id}/transactions/`
   * @example
   *
   * ```tsx
   * const { data, isLoading } =
   *   rawApi.useGetAggregatorInstitutionsInstitutionIdTransactionsRawQuery({
   *     institution_id: 'ins_1234',
   *     start_date: '2024-01-01',
   *     end_date: '2024-12-31',
   *     refresh: false,
   *     client_id: 123,
   *   });
   *
   * if (data && 'job_id' in data) {
   *   console.log('Job in progress:', data.job_id);
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: ID of the financial institution (path)
   *   - `start_date`: Start of the date range (e.g. 2024-01-01)
   *   - `end_date`: End of the date range (e.g. 2024-12-31)
   *   - `refresh`: (Optional) Force refresh from provider
   *   - `client_id`: (Optional) Client ID for data retrieval
   *
   * @returns {@link BrokerageTypes.GetAggregatorInstitutionsInstitutionIdTransactionsResponseBody}
   *   (e.g. {@link BrokerageTypes.InProgressResponse} when retrieval is async).
   */
  getAggregatorInstitutionsInstitutionIdTransactionsRaw: build.query<
    BrokerageTypes.GetAggregatorInstitutionsInstitutionIdTransactionsResponseBody,
    BrokerageTypes.GetAggregatorInstitutionsInstitutionIdTransactionsParams
  >({
    query: ({ institution_id, start_date, end_date, refresh, client_id }) => ({
      url: `/v2/aggregator/institutions/${institution_id}/transactions/`,
      method: 'GET',
      params: {
        start_date,
        end_date,
        refresh,
        client_id,
      },
    }),
  }),

  /**
   * Add a list of transactions manually for a linked institution.
   *
   * Inserts manually entered transactions for a given aggregator institution. Used
   * to add historical or corrected transaction data that is not provided by the
   * institution’s sync.
   *
   * @remarks
   * The request body is an array of
   * {@link BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdTransactionsRequestBody}.
   * Path parameter institution_id and optional query client_id identify the
   * context.
   *
   * **Raw endpoint:** `POST
   * /v2/aggregator/institutions/{institution_id}/transactions/`
   * @example
   *
   * ```tsx
   * const [addTransactions, { isLoading, data }] =
   *   rawApi.useCreateAggregatorInstitutionsInstitutionIdTransactionsRawMutation();
   *
   * await addTransactions({
   *   institution_id: 'ins_1234',
   *   client_id: 123,
   *   // ...request body: see CreateAggregatorInstitutionsInstitutionIdTransactionsRequestBody
   * });
   *
   * if (data) {
   *   console.log('Created transactions:', data.length);
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `institution_id`: ID of the financial institution (path)
   *   - `client_id`: (Optional) Client ID for data retrieval
   *   - Remaining fields form the request body per
   *       {@link BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdTransactionsRequestBody}
   *       (array of {@link BrokerageTypes.AccountTransactionsInput})
   *
   * @returns An array of
   *   {@link BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdTransactionsResponseBody}
   *   with the created transaction records.
   */
  createAggregatorInstitutionsInstitutionIdTransactionsRaw: build.mutation<
    BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdTransactionsResponseBody,
    BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdTransactionsParams &
      BrokerageTypes.CreateAggregatorInstitutionsInstitutionIdTransactionsRequestBody
  >({
    query: ({ institution_id, client_id, ...body }) => ({
      url: `/v2/aggregator/institutions/${institution_id}/transactions/`,
      method: 'POST',
      body,
      params: {
        client_id,
      },
    }),
    invalidatesTags: [],
  }),
});

const getAssetsEndpoints = (build: EndpointBuilders) => ({
  /**
   * Validate if a broker supports trades for a list of tickers.
   *
   * Checks whether a specific broker supports trading for a list of ticker symbols.
   * This is useful for validating asset availability before placing orders or
   * building trading strategies.
   *
   * @remarks
   * This endpoint validates asset coverage by checking if each ticker can be traded
   * on the specified broker platform. It returns validation results indicating
   * whether each ticker is available, unavailable, or has restrictions. Use this to
   * ensure assets are tradeable before attempting to place orders.
   *
   * **Raw endpoint:** `POST /v2/assets/coverage/validation/`
   * @example
   *
   * ```tsx
   * const [validateCoverage, { isLoading, data: validationResults }] =
   *   rawApi.useCreateAssetsCoverageValidationRawMutation();
   *
   * await validateCoverage({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   tickers: ['AAPL', 'GOOGL', 'TSLA', 'MSFT'],
   *   account_id: 'DU1234567', // Optional
   * });
   *
   * // Check validation results
   * validationResults?.forEach(result => {
   *   console.log(`${result.ticker}: ${result.availability}`);
   *   // 'available', 'unavailable', 'restricted', etc.
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `tickers`: Array of ticker symbols to validate
   *   - `account_id`: (Optional) Account ID for validation context
   *
   * @returns An array of
   *   {@link BrokerageTypes.CreateAssetsCoverageValidationResponseBody} containing
   *   validation results for each ticker including ticker symbol and availability
   *   status.
   */
  createAssetsCoverageValidationRaw: build.mutation<
    BrokerageTypes.CreateAssetsCoverageValidationResponseBody,
    BrokerageTypes.CreateAssetsCoverageValidationParams &
      BrokerageTypes.CreateAssetsCoverageValidationRequestBody
  >({
    query: ({ broker_key, type_account, ...body }) => ({
      url: `/v2/assets/coverage/validation/`,
      method: 'POST',
      body,
      params: {
        broker_key,
        type_account,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Get asset details for stocks.
   *
   * Retrieves detailed information about stock assets including ticker, market, and
   * name. Can fetch details for a single ticker (via query parameter) or multiple
   * tickers (via request body).
   *
   * @remarks
   * This endpoint returns asset metadata for stocks. You can query a single ticker
   * using the `ticker` query parameter, or multiple tickers using the `stocks`
   * array in the request body. The response includes market information and asset
   * names for each ticker.
   *
   * **Raw endpoint:** `POST /v2/assets/stocks/`
   * @example
   *
   * ```tsx
   * // Single ticker via query parameter
   * const [getAsset, { isLoading, data: asset }] =
   *   rawApi.useCreateAssetsStocksRawMutation();
   *
   * await getAsset({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   ticker: 'AAPL',
   * });
   *
   * // Multiple tickers via body
   * await getAsset({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   stocks: ['AAPL', 'GOOGL', 'MSFT'],
   * });
   *
   * // Access asset details
   * asset?.forEach(asset => {
   *   console.log(`${asset.ticker}: ${asset.name}`);
   *   console.log(`Market: ${asset.market}`);
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `ticker`: (Optional) Single ticker symbol to get details for (query param)
   *   - `stocks`: (Optional) Array of ticker symbols to get details for (body)
   *
   * @returns An array of {@link BrokerageTypes.CreateAssetsStocksResponseBody}
   *   containing asset information including ticker, market, and name for each
   *   stock.
   */
  createAssetsStocksRaw: build.mutation<
    BrokerageTypes.CreateAssetsStocksResponseBody,
    BrokerageTypes.CreateAssetsStocksParams &
      BrokerageTypes.CreateAssetsStocksRequestBody
  >({
    query: ({ broker_key, type_account, ticker, stocks, ...body }) => ({
      url: `/v2/assets/stocks/`,
      method: 'POST',
      body: stocks,
      params: {
        broker_key,
        type_account,
        ticker,
      },
    }),
    invalidatesTags: [],
  }),
});

const getCurrencyEndpoints = (build: EndpointBuilders) => ({
  /**
   * Get currency conversion rates for currency pairs.
   *
   * Retrieves current exchange rates for specified currency pairs. Currency pairs
   * are specified as combined strings (e.g., "USDEUR" for USD to EUR conversion).
   *
   * @remarks
   * This endpoint returns real-time or latest available exchange rates for currency
   * pairs. Currency pairs are specified as combined strings where the first three
   * letters represent the base currency and the last three represent the quote
   * currency (e.g., "USDEUR" means 1 USD = X EUR).
   *
   * **Raw endpoint:** `POST /v2/currency/prices/`
   * @example
   *
   * ```tsx
   * const [getCurrencyPrices, { isLoading, data: prices }] =
   *   rawApi.useCreateCurrencyPricesRawMutation();
   *
   * await getCurrencyPrices({
   *   currencies: ['USDEUR', 'USDJPY', 'USDCOP', 'EURGBP'],
   * });
   *
   * // Access exchange rates
   * prices?.forEach(rate => {
   *   console.log(`${rate.pair}: ${rate.rate}`);
   *   // USDEUR: 0.85, USDJPY: 150.25, etc.
   * });
   * ```
   *
   * @param body - An object containing:
   *
   *   - `currencies`: Array of currency pair strings (e.g., ['USDEUR', 'USDJPY']) where
   *       each string combines base and quote currencies
   *
   * @returns An array of currency exchange rates including pair identifier and
   *   conversion rate.
   */
  createCurrencyPricesRaw: build.mutation<
    BrokerageTypes.CreateCurrencyPricesResponseBody,
    BrokerageTypes.CreateCurrencyPricesRequestBody
  >({
    query: ({ currencies }) => ({
      url: `/v2/currency/prices/`,
      method: 'POST',
      body: currencies,
    }),
    invalidatesTags: [],
  }),
});

const getEquityEndpoints = (build: EndpointBuilders) => ({
  /**
   * Get prices from Finnhub API for a list of tickers.
   *
   * Retrieves current stock prices from the Finnhub API for specified ticker
   * symbols. This uses Citec's internal price service which aggregates data from
   * Finnhub.
   *
   * @remarks
   * This endpoint fetches real-time or latest available prices from Finnhub through
   * Citec's price service. Prices are returned in the specified currency. If a
   * price cannot be fetched for a ticker, it will be marked as "not available". Use
   * this when you need prices independent of broker-specific data.
   *
   * **Raw endpoint:** `POST /v2/equity/citec/price/`
   * @example
   *
   * ```tsx
   * const [getCitecPrices, { isLoading, data: prices }] =
   *   rawApi.useCreateEquityCitecPriceRawMutation();
   *
   * await getCitecPrices({
   *   tickers: ['AAPL', 'GOOGL', 'MSFT', 'TSLA'],
   *   currency: 'USD', // Optional, defaults to USD
   * });
   *
   * // Access prices
   * prices?.forEach(price => {
   *   if (price.price !== 'not available') {
   *     console.log(`${price.ticker}: $${price.price}`);
   *   } else {
   *     console.log(`${price.ticker}: Price not available`);
   *   }
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `tickers`: Array of ticker symbols to get prices for
   *   - `currency`: (Optional) Currency for prices ('USD' or 'EUR'), defaults to 'USD'
   *
   * @returns An array of price objects containing ticker and price (or "not
   *   available" if price could not be fetched).
   */
  createEquityCitecPriceRaw: build.mutation<
    BrokerageTypes.CreateEquityCitecPriceResponseBody,
    BrokerageTypes.CreateEquityCitecPriceParams &
      BrokerageTypes.CreateEquityCitecPriceRequestBody
  >({
    query: ({ currency, tickers }) => ({
      url: `/v2/equity/citec/price/`,
      method: 'POST',
      body: tickers,
      params: {
        currency,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Get broker prices for a list of tickers.
   *
   * Retrieves current stock prices directly from the specified broker platform for
   * a list of ticker symbols. Prices are returned in the specified currency.
   *
   * @remarks
   * This endpoint fetches real-time or latest available prices from the broker's
   * API. Unlike the Citec price endpoint, this uses broker-specific pricing data
   * which may be more accurate for trading purposes. If a price cannot be fetched
   * for a ticker, it will be marked as "not available". Use this when you need
   * broker-specific pricing for order placement or portfolio valuation.
   *
   * **Raw endpoint:** `POST /v2/equity/prices/`
   * @example
   *
   * ```tsx
   * const [getBrokerPrices, { isLoading, data: prices }] =
   *   rawApi.useCreateEquityPricesRawMutation();
   *
   * await getBrokerPrices({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   tickers: ['AAPL', 'GOOGL', 'MSFT'],
   *   currency: 'USD', // Optional, defaults to USD
   * });
   *
   * // Access broker prices
   * prices?.forEach(price => {
   *   if (price.price !== 'not available') {
   *     console.log(`${price.ticker}: $${price.price} (from broker)`);
   *   } else {
   *     console.log(`${price.ticker}: Price not available from broker`);
   *   }
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `tickers`: Array of ticker symbols to get prices for
   *   - `currency`: (Optional) Currency for prices ('USD' or 'EUR'), defaults to 'USD'
   *
   * @returns An array of price objects containing ticker and price (or "not
   *   available" if price could not be fetched from the broker).
   */
  createEquityPricesRaw: build.mutation<
    BrokerageTypes.CreateEquityPricesResponseBody,
    BrokerageTypes.CreateEquityPricesParams &
      BrokerageTypes.CreateEquityPricesRequestBody
  >({
    query: ({ broker_key, type_account, currency, tickers }) => ({
      url: `/v2/equity/prices/`,
      method: 'POST',
      body: tickers,
      params: {
        broker_key,
        type_account,
        currency,
      },
    }),
    invalidatesTags: [],
  }),
});

const getNotificationEndpoints = (build: EndpointBuilders) => ({
  /**
   * Post a notification.
   *
   * Creates and sends a notification to the system. Notifications can be used for
   * various purposes such as alerting users about proposal updates, order status
   * changes, or other system events.
   *
   * @remarks
   * This endpoint creates a notification that can be displayed to users or used for
   * system alerts. The notification type and content are specified in the request
   * body. Common notification types include 'advisory_proposal', 'order_status',
   * 'portfolio_update', etc.
   *
   * **Raw endpoint:** `POST /v2/notification/`
   * @example
   *
   * ```tsx
   * const [postNotification, { isLoading, isSuccess }] =
   *   rawApi.useCreateNotificationRawMutation();
   *
   * await postNotification({
   *   type: 'advisory_proposal',
   *   client_id: 123,
   *   message: 'New proposal available for review',
   * });
   *
   * if (isSuccess) {
   *   console.log('Notification posted successfully');
   * }
   * ```
   *
   * @param body - An object containing:
   *
   *   - `type`: Notification type (e.g., 'advisory_proposal', 'order_status')
   *   - `client_id`: (Optional) Client ID associated with the notification
   *   - `message`: (Optional) Notification message content
   *   - Other notification-specific fields
   *
   * @returns Void response (201) upon successful notification creation.
   */
  createNotificationRaw: build.mutation<
    BrokerageTypes.CreateNotificationResponseBody,
    BrokerageTypes.CreateNotificationRequestBody
  >({
    query: body => ({
      url: `/v2/notification/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [],
  }),
});

const getPortfolioEndpoints = (build: EndpointBuilders) => ({
  /**
   * Delete active orders from the user's broker account.
   *
   * Cancels and removes active orders that have been placed in the broker account.
   * This is used to cancel pending orders before they are executed.
   *
   * @remarks
   * This endpoint cancels active orders by their IDs. Only orders that are still
   * pending or active can be deleted. Filled, cancelled, or rejected orders cannot
   * be deleted. The response returns the updated list of orders after deletion.
   *
   * **Raw endpoint:** `DELETE /v2/portfolio/orders/`
   * @example
   *
   * ```tsx
   * const [deleteOrders, { isLoading, data: updatedOrders }] =
   *   rawApi.useDeletePortfolioOrdersRawMutation();
   *
   * await deleteOrders({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   account_id: 'DU1234567',
   *   orders: [
   *     { id: '549775566', ticker: 'TSLA', stocks_number: 5, side: 'buy' },
   *     { id: '549775567', ticker: 'AAPL', stocks_number: 10, side: 'sell' },
   *   ],
   * });
   *
   * // Check updated orders
   * updatedOrders?.forEach(order => {
   *   console.log(`Order ${order.id}: ${order.status}`);
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `account_id`: Account ID where orders are placed
   *   - `orders`: Array of order objects to delete, each containing id, ticker,
   *       stocks_number, side, and status
   *
   * @returns An array of updated order objects after deletion.
   */
  deletePortfolioOrdersRaw: build.mutation<
    BrokerageTypes.DeletePortfolioOrdersResponseBody,
    BrokerageTypes.DeletePortfolioOrdersParams &
      BrokerageTypes.DeletePortfolioOrdersRequestBody
  >({
    query: ({ broker_key, type_account, ...body }) => ({
      url: `/v2/portfolio/orders/`,
      method: 'DELETE',
      body,
      params: {
        broker_key,
        type_account,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Update active orders in the user's broker account.
   *
   * Modifies the quantity, side, or other properties of active orders that have
   * been placed in the broker account. This is used to adjust orders before they
   * are fully executed.
   *
   * @remarks
   * This endpoint allows modification of active orders by their order IDs. You can
   * update the quantity (stocks_number), side (buy/sell), or other order
   * properties. Only orders that are still pending or partially filled can be
   * updated. The response returns the updated list of orders.
   *
   * **Raw endpoint:** `PATCH /v2/portfolio/orders/`
   * @example
   *
   * ```tsx
   * const [updateOrders, { isLoading, data: updatedOrders }] =
   *   rawApi.useUpdatePortfolioOrdersRawMutation();
   *
   * await updateOrders({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   account_id: 'DU1234567',
   *   orders: [
   *     {
   *       order_id: 'a0s7dfa-as78d',
   *       ticker: 'TSLA',
   *       stocks_number: 10, // Updated quantity
   *       side: 'buy',
   *       status: 'pending',
   *     },
   *   ],
   * });
   *
   * // Check updated orders
   * updatedOrders?.forEach(order => {
   *   console.log(
   *     `Order ${order.order_id}: ${order.stocks_number} ${order.ticker}`,
   *   );
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `account_id`: Account ID where orders are placed
   *   - `orders`: Array of order objects to update, each containing order_id, ticker,
   *       stocks_number, side, and status
   *
   * @returns An array of updated order objects.
   */
  updatePortfolioOrdersRaw: build.mutation<
    BrokerageTypes.UpdatePortfolioOrdersResponseBody,
    BrokerageTypes.UpdatePortfolioOrdersParams &
      BrokerageTypes.UpdatePortfolioOrdersRequestBody
  >({
    query: ({ broker_key, type_account, ...body }) => ({
      url: `/v2/portfolio/orders/`,
      method: 'PATCH',
      body,
      params: {
        broker_key,
        type_account,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Place new orders to trade in the user's broker account.
   *
   * Creates and submits new trading orders to the broker platform. Orders can be
   * buy or sell orders for specified tickers and quantities.
   *
   * @remarks
   * This endpoint places new orders on the broker platform. Each order specifies a
   * ticker, quantity (stocks_number), and side (buy or sell). Orders are submitted
   * immediately and will be executed according to market conditions. The response
   * returns the created orders with their assigned order IDs and initial status.
   *
   * **Raw endpoint:** `POST /v2/portfolio/orders/`
   * @example
   *
   * ```tsx
   * const [placeOrders, { isLoading, data: createdOrders }] =
   *   rawApi.useCreatePortfolioOrdersRawMutation();
   *
   * await placeOrders({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   account_id: 'DU1234567',
   *   orders: [
   *     { ticker: 'TSLA', stocks_number: 5, side: 'buy' },
   *     { ticker: 'AAPL', stocks_number: 10, side: 'buy' },
   *   ],
   * });
   *
   * // Check created orders
   * createdOrders?.forEach(order => {
   *   console.log(`Order ${order.order_id}: ${order.status}`);
   *   console.log(`${order.side} ${order.stocks_number} ${order.ticker}`);
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `account_id`: Account ID to place orders in
   *   - `orders`: Array of order objects to place, each containing ticker,
   *       stocks_number, and side ('buy' or 'sell')
   *
   * @returns An array of created order objects including order_id, status, ticker,
   *   stocks_number, and side.
   */
  createPortfolioOrdersRaw: build.mutation<
    BrokerageTypes.CreatePortfolioOrdersResponseBody,
    BrokerageTypes.CreatePortfolioOrdersParams &
      BrokerageTypes.CreatePortfolioOrdersRequestBody
  >({
    query: ({ broker_key, type_account, ...body }) => ({
      url: `/v2/portfolio/orders/`,
      method: 'POST',
      body,
      params: {
        broker_key,
        type_account,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Search and filter orders from the user's broker account.
   *
   * Retrieves a filtered list of orders from the broker account based on search
   * criteria. This allows querying orders by status, ticker, date range, or other
   * filters.
   *
   * @remarks
   * This endpoint uses POST method to accept complex filter criteria in the request
   * body. You can filter orders by status (pending, filled, cancelled, etc.),
   * ticker, date range, side (buy/sell), or other order properties. The
   * `account_id` is optional; if omitted, orders from all accounts may be
   * returned.
   *
   * **Raw endpoint:** `POST /v2/portfolio/orders/search/`
   * @example
   *
   * ```tsx
   * const [searchOrders, { isLoading, data: filteredOrders }] =
   *   rawApi.useCreatePortfolioOrdersSearchRawMutation();
   *
   * await searchOrders({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   account_id: 'DU1234567', // Optional
   *   order_filter: {
   *     status: 'filled',
   *     ticker: 'TSLA',
   *     start_date: '2024-01-01',
   *     end_date: '2024-12-31',
   *   },
   * });
   *
   * // Process filtered orders
   * filteredOrders?.forEach(order => {
   *   console.log(
   *     `${order.ticker}: ${order.status} - ${order.side} ${order.stocks_number}`,
   *   );
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `account_id`: (Optional) Account ID to filter orders
   *   - `order_filter`: Filter criteria object including status, ticker, date range,
   *       side, or other order properties
   *
   * @returns An array of order objects matching the filter criteria.
   */
  createPortfolioOrdersSearchRaw: build.mutation<
    BrokerageTypes.CreatePortfolioOrdersSearchResponseBody,
    BrokerageTypes.CreatePortfolioOrdersSearchParams &
      BrokerageTypes.CreatePortfolioOrdersSearchRequestBody
  >({
    query: ({ broker_key, type_account, ...body }) => ({
      url: `/v2/portfolio/orders/search/`,
      method: 'POST',
      body,
      params: {
        broker_key,
        type_account,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Get portfolio performance metrics.
   *
   * Calculates and returns portfolio performance data for a specified time period.
   * This includes returns, gains/losses, and other performance metrics.
   *
   * @remarks
   * This endpoint calculates portfolio performance over a specified period (1 day,
   * 1 week, 1 month, or 1 year). Performance metrics include total return,
   * percentage return, absolute gains/losses, and other key performance indicators.
   * All values are returned in the specified currency.
   *
   * **Raw endpoint:** `POST /v2/portfolio/performance/`
   * @example
   *
   * ```tsx
   * const [getPerformance, { isLoading, data: performance }] =
   *   rawApi.useCreatePortfolioPerformanceRawMutation();
   *
   * await getPerformance({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   account_id: 'DU1234567', // Optional
   *   period: '1M', // '1D', '1W', '1M', or '1A'
   *   currency: 'USD', // Optional, defaults to USD
   * });
   *
   * // Access performance metrics
   * if (performance) {
   *   console.log(`Total return: ${performance.total_return}%`);
   *   console.log(
   *     `Absolute return: ${performance.absolute_return} ${performance.currency}`,
   *   );
   *   console.log(`Period: ${performance.period}`);
   * }
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `account_id`: (Optional) Account ID to get performance for
   *   - `period`: Time period for performance calculation ('1D', '1W', '1M', or '1A')
   *   - `currency`: (Optional) Currency for performance values ('USD' or 'EUR'),
   *       defaults to 'USD'
   *
   * @returns Portfolio performance data including total return, absolute return,
   *   period, currency, and other performance metrics.
   */
  createPortfolioPerformanceRaw: build.mutation<
    BrokerageTypes.CreatePortfolioPerformanceResponseBody,
    BrokerageTypes.CreatePortfolioPerformanceParams &
      BrokerageTypes.CreatePortfolioPerformanceRequestBody
  >({
    query: ({ broker_key, type_account, period, currency, ...body }) => ({
      url: `/v2/portfolio/performance/`,
      method: 'POST',
      body,
      params: {
        broker_key,
        type_account,
        period,
        currency,
      },
    }),
    invalidatesTags: [],
  }),

  /**
   * Get portfolio positions from the broker.
   *
   * Retrieves current portfolio positions (holdings) from the broker account. This
   * includes all open positions with their quantities, prices, and values.
   *
   * @remarks
   * This endpoint fetches all current positions from the broker account. Positions
   * include stocks, ETFs, and other securities currently held. Each position
   * includes ticker, quantity, average price, current price, market value, and
   * unrealized gains/losses. All values are returned in the specified currency.
   *
   * **Raw endpoint:** `POST /v2/portfolio/positions/`
   * @example
   *
   * ```tsx
   * const [getPositions, { isLoading, data: positions }] =
   *   rawApi.useCreatePortfolioPositionsRawMutation();
   *
   * await getPositions({
   *   broker_key: 'alpaca',
   *   type_account: 'PAPER',
   *   account_id: 'DU1234567', // Optional
   *   currency: 'USD', // Optional, defaults to USD
   * });
   *
   * // Process positions
   * positions?.forEach(position => {
   *   console.log(
   *     `${position.ticker}: ${position.quantity} @ $${position.avg_price}`,
   *   );
   *   console.log(`Market value: $${position.market_value}`);
   *   console.log(`Unrealized P/L: $${position.unrealized_pl}`);
   * });
   * ```
   *
   * @param params - An object containing:
   *
   *   - `broker_key`: One of the broker keys ('alpaca', 'interactive_brokers',
   *       'tradier')
   *   - `type_account`: Account type ('PAPER' or 'LIVE')
   *   - `account_id`: (Optional) Account ID to get positions for
   *   - `currency`: (Optional) Currency for position values ('USD' or 'EUR'), defaults
   *       to 'USD'
   *
   * @returns An array of position objects including ticker, quantity, average
   *   price, current price, market value, unrealized gains/losses, and currency.
   */
  createPortfolioPositionsRaw: build.mutation<
    BrokerageTypes.CreatePortfolioPositionsResponseBody,
    BrokerageTypes.CreatePortfolioPositionsParams &
      BrokerageTypes.CreatePortfolioPositionsRequestBody
  >({
    query: ({ broker_key, type_account, currency, ...body }) => ({
      url: `/v2/portfolio/positions/`,
      method: 'POST',
      body,
      params: {
        broker_key,
        type_account,
        currency,
      },
    }),
    invalidatesTags: [],
  }),
});

const getSettingsEndpoints = (build: EndpointBuilders) => ({
  /**
   * Root endpoint for proxy server status checks.
   *
   * Simple health check endpoint that returns the API status. This is typically
   * used by load balancers, monitoring systems, or proxy servers to verify that the
   * API is accessible and responding.
   *
   * @remarks
   * This is a basic health check endpoint that doesn't require authentication. It
   * returns a simple status response indicating that the API is operational. Use
   * this for infrastructure monitoring and health checks.
   *
   * **Raw endpoint:** `GET /`
   * @example
   *
   * ```tsx
   * const { data: status, isLoading } = rawApi.useGetRawQuery();
   *
   * // Check API status
   * if (status) {
   *   console.log('API is operational');
   * }
   * ```
   *
   * @returns API status information indicating the service is operational.
   */
  getRaw: build.query<BrokerageTypes.GetRawResponseBody, void>({
    query: () => ({
      url: `/`,
      method: 'GET',
    }),
  }),

  /**
   * Check authentication integration status.
   *
   * Verifies the authentication system integration and returns authentication
   * configuration or status information. This is used to check if authentication is
   * properly configured and operational.
   *
   * @remarks
   * This endpoint checks the authentication integration status. It may return
   * information about authentication providers, configuration, or system status.
   * Use this to verify that authentication services are properly integrated and
   * functioning.
   *
   * **Raw endpoint:** `GET /settings/auth/`
   * @example
   *
   * ```tsx
   * const { data: authStatus, isLoading } =
   *   rawApi.useGetSettingsAuthRawQuery();
   *
   * // Check auth integration
   * if (authStatus) {
   *   console.log('Auth integration status:', authStatus.status);
   *   console.log('Providers:', authStatus.providers);
   * }
   * ```
   *
   * @returns Authentication integration status and configuration information.
   */
  getSettingsAuthRaw: build.query<BrokerageTypes.GetSettingsAuthResponseBody, void>(
    {
      query: () => ({
        url: `/settings/auth/`,
        method: 'GET',
      }),
    },
  ),

  /**
   * Public ping endpoint (no authentication required).
   *
   * Simple health check endpoint that doesn't require authentication. Returns a
   * ping response to verify the API is accessible and responding to requests.
   *
   * @remarks
   * This is a public endpoint that can be accessed without authentication. It's
   * useful for basic health checks, monitoring, or verifying API accessibility from
   * external systems. Use this when you need to check API availability without
   * providing credentials.
   *
   * **Raw endpoint:** `GET /settings/ping/`
   * @example
   *
   * ```tsx
   * const { data: pingResponse, isLoading } =
   *   rawApi.useGetSettingsPingRawQuery();
   *
   * // Check API availability
   * if (pingResponse) {
   *   console.log('API is responding:', pingResponse.message || 'OK');
   * }
   * ```
   *
   * @returns Ping response indicating the API is accessible and responding.
   */
  getSettingsPingRaw: build.query<BrokerageTypes.GetSettingsPingResponseBody, void>(
    {
      query: () => ({
        url: `/settings/ping/`,
        method: 'GET',
      }),
    },
  ),

  /**
   * Private ping endpoint (authentication required).
   *
   * Health check endpoint that requires valid authentication. Returns a ping
   * response to verify both API accessibility and authentication status.
   *
   * @remarks
   * This endpoint requires a valid access token. It's useful for verifying that
   * authentication is working correctly and that the API is accessible with the
   * current credentials. Use this to test authentication flow or verify token
   * validity.
   *
   * **Raw endpoint:** `GET /settings/ping/private/`
   * @example
   *
   * ```tsx
   * const {
   *   data: pingResponse,
   *   isLoading,
   *   error,
   * } = rawApi.useGetSettingsPingPrivateRawQuery();
   *
   * // Check authenticated API availability
   * if (pingResponse) {
   *   console.log(
   *     'Authenticated API is responding:',
   *     pingResponse.result || 'OK',
   *   );
   * } else if (error) {
   *   console.error('Authentication failed or API unavailable');
   * }
   * ```
   *
   * @returns Ping response indicating the authenticated API is accessible and the
   *   authentication token is valid.
   */
  getSettingsPingPrivateRaw: build.query<
    BrokerageTypes.GetSettingsPingPrivateResponseBody,
    void
  >({
    query: () => ({
      url: `/settings/ping/private/`,
      method: 'GET',
    }),
  }),
});

const getWebhooksEndpoints = (build: EndpointBuilders) => ({
  /**
   * Receive and process Lleida.net signature webhook notifications.
   *
   * Webhook endpoint for receiving signature notifications from Lleida.net service.
   * The payload is expected to be a raw encrypted string in the request body that
   * will be decrypted and processed by the API.
   *
   * @remarks
   * This endpoint receives webhook notifications from Lleida.net regarding document
   * signatures. The payload is encrypted and will be automatically decrypted by the
   * API. This endpoint is typically called by Lleida.net's webhook system, not
   * directly by client applications.
   *
   * **Raw endpoint:** `POST /v2/webhooks/lleidanet/signature`
   * @example
   *
   * ```tsx
   * // Note: This endpoint is typically called by Lleida.net webhook system
   * // Direct usage in client applications is rare
   * const [processWebhook, { isLoading }] =
   *   rawApi.useCreateWebhooksLleidanetSignatureRawMutation();
   *
   * // The encrypted payload would be sent by Lleida.net
   * // await processWebhook();
   * ```
   *
   * @returns Webhook processing confirmation or status.
   */
  createWebhooksLleidanetSignatureRaw: build.mutation<
    BrokerageTypes.CreateWebhooksLleidanetSignatureResponseBody,
    void
  >({
    query: () => ({
      url: `/v2/webhooks/lleidanet/signature`,
      method: 'POST',
    }),
  }),
});

export const rawApi = brokerageApiV2.injectEndpoints({
  endpoints: build => ({
    ...getAccountEndpoints(build),
    ...getAdvisoryEndpoints(build),
    ...getAggregatorEndpoints(build),
    ...getAggregatorInstitutionsEndpoints(build),
    ...getAssetsEndpoints(build),
    ...getCurrencyEndpoints(build),
    ...getEquityEndpoints(build),
    ...getNotificationEndpoints(build),
    ...getPortfolioEndpoints(build),
    ...getSettingsEndpoints(build),
    ...getWebhooksEndpoints(build),
  }),
});
